import { signal } from '@angular/core';
import type { UIMessage } from 'ai';
import { firstValueFrom } from 'rxjs';
import { v4 } from 'uuid';
import { AI_CHAT_CONTEXT_LIMIT_REACHED_CODE, AI_DURABLE_RUN_COMPLETION_METADATA_KEY, isAIChatContextGuardStatus } from '@jms/model';
import type { AIAgentAttachment, AIAgentClientContext, AIChatContextGuardStatus, AIDurablePendingSteeringEntry, AIDurableRunCompletionReason, AIDurableSendResponse, AIDurableStopResponse, AIDurableThreadState, AIDurableThreadStatusResponse, AIInterviewResponse } from '@jms/model';
import type { AIService } from '@workbuddy/ai/core/ai.service';
import { AgentUtility } from '../agent.utility';
import { AGENT_CARD_EXPIRY_MS, buildRunCompletion, isCardExpired, readRunCompletion } from '../agent.run.ending';
import type { AnyPart, PendingFile } from '../agent.types';
import type { AgentChatAdapter, AgentChatStatus, AgentTurnRequest } from './agent.chat.adapter';
import { AgentMessageState } from './agent.message.state';
import { DURABLE_USAGE_LIMIT_CODE, DurableReplayFilter, resolveSendIntent, toDurableRouteError } from './durable.agent.transport';
import type { DurableAgentTransport } from './durable.agent.transport';
import type { DurableRouteError } from './errors';
import type { DurableSubscriptionEvent, DurableSubscriptionHandle } from './durable.agent.transport.types';
import type { DurableAdapterClock, DurableFailedSend, PaintedTurn } from './durable.workbuddy.agent.chat.adapter.types';

/**
 * Chat adapter for the durable Workbuddy Agent runtime (release flag AIDurableRuns).
 *
 * The browser never owns a run here. Opening a thread is hydrate (messages route), then status and
 * subscribe TOGETHER; the subscription is the only live connection and it is never used to end
 * anything. Losing it reconnects with backoff. Sending while the agent works is steering: the text
 * becomes a queued card above the composer until the loop echoes it back, at which point it joins
 * the transcript. Stop goes through the stop route and hands unconsumed steering back to the composer.
 * How a run ended is read from the completion record on the final assistant message, written here
 * from a status read the moment the live stream ends and again from storage on the next hydrate.
 *
 * Feeds the same UI message state the request-bound adapter feeds: the message list, reveal service,
 * cards, download cards and context guard are unchanged.
 */

/** Reconnect backoff after a dropped subscription: 1s, 2s, 4s, ... capped. */
export const DURABLE_RECONNECT_BASE_MS = 1_000;
export const DURABLE_RECONNECT_MAX_MS = 15_000;
/** Reconnects before the window gives up (about two and a half minutes at the backoff above): long enough to ride out a deploy, short enough not to retry for ever while the agent is down. */
const DURABLE_RECONNECT_MAX_ATTEMPTS = 12;
/** How long a subscription must stay open before its drop no longer counts towards the reconnect cap. */
const DURABLE_HEALTHY_CONNECTION_MS = 30_000;
/** How long a failed "is this run live?" read waits before the next chunk of that run may ask again. */
const UNDECIDED_RUN_RETRY_MS = 2_000;

/**
 * How long a run this window believes is working may stay silent before its status is re-read, and
 * how often the read repeats while the silence lasts.
 *
 * A subscription is not a promise of a terminal chunk: a model stream that errors and retries inside
 * a run has published nothing to subscribers, so every window keeps its Stop button and its queued
 * cards while the thread is already finished. The poll is that safety net, not the normal path — a
 * run whose chunks are arriving asks the status route nothing, and the timer stops the moment the
 * thread is no longer believed to be working.
 */
export const DURABLE_ACTIVE_POLL_INTERVAL_MS = 30_000;

/**
 * How soon a Stop the server could not confirm asks again, doubling up to the silence-poll interval.
 *
 * The stop route ends its confirmation window on a deadline, and a deadline is not proof that work
 * stopped — the run can still be inside a long tool call. So the window keeps saying Stopping and
 * keeps asking, rather than clearing the thread's queued guidance on a guess. The server keeps that
 * guidance too until a stop is confirmed; once the thread reads quiet this window stops again, which
 * is when the server hands the guidance back and removes it for every window.
 */
export const DURABLE_STOP_VERIFY_BASE_MS = 2_000;

/**
 * Chunk types that mean the run is producing this window's current reply, whether they arrive live
 * or as the whole-run replay a reattach (or a reload) receives mid-run. Any of them binds the run to
 * its reply bubble and puts the window into the active state; terminal chunks, the context-guard
 * measurement and `start` are deliberately absent. `start` carries no content and always precedes a
 * `step-start`, which is where the replay of a step storage already holds is recognised and skipped;
 * opening a bubble on `start` would open one for exactly those steps.
 */
const RUN_CONTENT_CHUNK_TYPES = new Set([
  'step-start',
  'text-start',
  'text-delta',
  'reasoning-start',
  'reasoning-delta',
  'tool-call-input-streaming-start',
  'tool-call-delta',
  'tool-call-input-streaming-end',
  'tool-call',
  'tool-call-approval',
  'tool-output-denied',
  'tool-result',
  'tool-error',
  'data-user-message',
]);

/**
 * Status re-reads after a run ends while status still reports it running: 0.5 s, then doubling, five
 * reads within about eight seconds, which covers the couple of seconds a finished run takes to settle.
 */
const RUN_END_RECHECK_BASE_MS = 500;
const RUN_END_RECHECK_ATTEMPTS = 4;

/** How long after a card's expiry its status check runs, so a browser clock a little behind still reads it as expired. */
const CARD_EXPIRY_CHECK_MARGIN_MS = 1_000;

/** Route errors that mean "try again shortly", shown as a calm notice rather than a red error. */
const CALM_RETRY_CODES = new Set(['REDIS_UNAVAILABLE', 'RUNTIME_DRAINING', 'DURABLE_RUNTIME_UNAVAILABLE', 'UPSTREAM_UNREACHABLE']);

/** Subscription failures that no amount of reconnecting will fix. */
const PERMANENT_SUBSCRIBE_STATUSES = new Set([401, 403, 404]);

/** Shown while the server holds a card this window has no card of its own to answer it with. */
const DECIDE_FIRST_NOTICE = 'Answer the waiting card first: approve it, decline it, or stop the run.';

/**
 * Shown when a send carrying a file arrives at a thread another window is already running.
 *
 * Guidance sent into a live run is delivered as text only, so the file would be dropped on the way.
 * The send is refused whole — words back to the composer, file still attached — unless the server
 * had already turned the send into guidance before this window could know, which is the second copy.
 */
const ATTACHMENT_REFUSED_NOTICE = 'The agent is already working and can only take text right now. Your message and file are still here — send them once it finishes.';
const ATTACHMENT_NOT_SENT_NOTICE = 'The agent was already working, so your message went in as guidance. Files cannot join a run, so yours is still attached — send it once this one finishes.';
/** How many recent sends keep their composer upload records; Retry only ever needs the latest few. */
const MAX_REMEMBERED_UPLOADS = 20;

/** Shown while a Stop waits for the server to confirm the run really ended. */
const STOP_UNCONFIRMED_NOTICE = 'Could not confirm the stop. The agent may still be working.';
const CONNECTION_LOST_NOTICE = 'Lost the connection to the agent. It may still be working; reopen this chat to check.';

/** The first event of every subscription: the run holding the thread lease when it opened. */
const SUBSCRIPTION_PREAMBLE_TYPE = 'workbuddy-subscription';

const DEFAULT_CLOCK: DurableAdapterClock = {
  now: () => Date.now(),
  setTimeout: (handler, ms) => setTimeout(handler, ms),
  clearTimeout: (handle) => clearTimeout(handle),
};

function isRecord(value: unknown): value is Record<string, unknown> {

  return typeof value === 'object' && value !== null;
}

function readString(record: Record<string, unknown> | undefined, key: string): string | undefined {

  const value = record?.[key];
  return typeof value === 'string' ? value : undefined;
}

/** Text of an echoed steering signal: Mastra sends a plain string for a single text part, else parts. */
function signalContentsText(contents: unknown): string {

  if (typeof contents === 'string') return contents;
  if (!Array.isArray(contents)) return '';
  return contents.map(part => (isRecord(part) && typeof part['text'] === 'string' ? part['text'] : '')).filter(Boolean).join('\n');
}

export class DurableWorkbuddyAgentChatAdapter implements AgentChatAdapter {

  private readonly state = new AgentMessageState();
  private readonly $status = signal<AgentChatStatus>('idle');
  private readonly $error = signal<Error | null>(null);
  private readonly $contextGuardStatus = signal<AIChatContextGuardStatus | null>(null);
  private readonly $pendingSteering = signal<AIDurablePendingSteeringEntry[]>([]);
  private readonly $threadState = signal<AIDurableThreadState | null>(null);
  private readonly $decideFirstMessage = signal<string | null>(null);
  private readonly $notice = signal<string | null>(null);
  private readonly $composerRestore = signal<string | null>(null);
  private readonly $failedSend = signal<DurableFailedSend | null>(null);
  /**
   * The send whose files never travelled and are the composer's to put back, or null: a refused
   * send, or a file that cannot join a run that is already working.
   */
  private readonly $heldRequestId = signal<string | null>(null);
  /**
   * The composer's upload records for recent sends, keyed by user message id. Kept here rather than
   * in the chat service because this adapter outlives the service when it is pooled: a chat view that
   * is recreated reattaches the adapter, and a refused send's files must come back with it.
   */
  private readonly uploads = new Map<string, PendingFile[]>();
  /** True from an unconfirmed Stop until the server says the thread is no longer working. */
  private readonly $stopPending = signal(false);
  /** Sends (starts and steers) the send route has not answered yet. */
  private readonly $sendsOutstanding = signal(0);
  /**
   * The stop still being settled with the server after the window already showed the run as
   * stopped. A send waits for it, so a new turn never races the run it replaces for the thread.
   */
  private stopTask: Promise<void> | null = null;
  /** Resolves the stop task once an unconfirmed stop settles in the background (or the thread is left). */
  private resolveStopSettled: (() => void) | null = null;
  /** Count of sends and card answers the usage-limit door check refused (429); the host re-reads the limit status. */
  private readonly $usageLimitRejections = signal(0);

  private readonly toolInputBuffers = new Map<string, string>();
  /**
   * The window's replay memory, deliberately owned here rather than by a subscription: it is handed
   * to every `subscribe` call so a reconnect keeps dropping the event ids this window has applied.
   */
  private readonly replayFilter = new DurableReplayFilter();
  /** Runs whose chunks this window applies: started here, or confirmed live by a status read. */
  private readonly acceptedRunIds = new Set<string>();
  /** Runs a status read said are not live (replayed history); their chunks are dropped. */
  private readonly rejectedRunIds = new Set<string>();
  /**
   * Runs this window has watched end. jms-ai replays a whole run to every new subscription, so the
   * reconnect that follows a finished run would otherwise re-apply it into a second reply bubble.
   * Sealing the run here drops that replay at the door for as long as the window lives, and lets the
   * filter release the run's event ids without opening the door again.
   */
  private readonly endedRunIds = new Set<string>();
  /** Chunks of a run seen before a status read decided whether it is live. */
  private readonly undecidedRunChunks = new Map<string, DurableSubscriptionEvent[]>();
  /** Runs with a status read in flight to settle that question; one read per run at a time. */
  private readonly undecidedRunReads = new Set<string>();
  /** Runs started by another window: their persisted messages are re-read once they end. */
  private readonly foreignRunIds = new Set<string>();
  /**
   * Every reply bubble a run has streamed into, oldest first; the last entry is the live one. A
   * replayed `start` reuses it instead of opening a second reply, and when the run ends the whole
   * list is what storage supersedes. Kept for the window's lifetime, ended runs included, so the
   * binding between a run and its reply can never be rebuilt from scratch by a later replay.
   */
  private readonly runBubbleIds = new Map<string, string[]>();
  /** Steering Stop already handed back to the composer; a lagging status read must not re-queue it. */
  private readonly stoppedSignalIds = new Set<string>();
  /**
   * Runs whose replay is inside a step the transcript already holds. Every chunk of the run is
   * dropped until its next `step-start` (or its end) says which message it is writing now.
   */
  private readonly replayingPersistedStep = new Set<string>();

  private threadId: string | null = null;
  private subscription: DurableSubscriptionHandle | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  /** The run whose final step has finished but whose `finish` has not arrived yet; Stop leaves it alone. */
  private replyCompleteRunId: string | null = null;
  /** A 401 on subscribe gets one retry with a fresh token (a stale nonce or session version), then counts as final. */
  private unauthorizedRetried = false;
  private activityPollTimer: ReturnType<typeof setTimeout> | null = null;
  /** When the last accepted chunk arrived; 0 while this window has seen none. */
  private lastChunkAtMs = 0;
  /** Bumped on every attach/reset so late async results for an earlier thread are ignored. */
  private generation = 0;
  private statusRead: Promise<AIDurableThreadStatusResponse | null> | null = null;
  /** The attachment generation the outstanding status read applies its result under. */
  private statusReadGeneration = 0;
  /**
   * Card answers this window has seen land: one it sent that the server accepted, or a card's outcome
   * that arrived over the subscription (answered in another window). A waiting reading taken before
   * either is outdated.
   */
  private cardAnswersSeen = 0;
  private pendingTurnUserMessageId: string | null = null;
  private pendingTurnAssistantMessageId: string | null = null;
  /** True from the moment Send paints the turn until the send route has answered for it. */
  private sendInFlight = false;
  /** A turn painted by `beginTurn`, waiting for the send that will claim it. */
  private paintedTurn: PaintedTurn | null = null;
  /** The next status re-read after a run ended while status still reported it running. */
  private runEndRecheckTimer: ReturnType<typeof setTimeout> | null = null;
  /** The one status check that retires a card left open past its expiry. */
  private cardExpiryTimer: ReturnType<typeof setTimeout> | null = null;
  private stopVerifyTimer: ReturnType<typeof setTimeout> | null = null;
  private stopVerifyAttempt = 0;
  /**
   * The runs an unconfirmed Stop was aimed at. The status route names the run holding the thread, so
   * a running thread under a different run id means the targeted run has ended and a newer one
   * (started from another window) took over, which this Stop must neither wait for nor stop.
   */
  private readonly stoppingRunIds = new Set<string>();
  /** True while the settling stop call is in flight, so a status read and a run ending never send two. */
  private stopSettling = false;
  /** Counts hydrations begun, so only the latest one may replace the transcript and attach. */
  private hydrations = 0;
  /** Counts Stop presses, so a send still preparing can tell it was stopped before it left. */
  private stopPresses = 0;
  /**
   * The start send still waiting for the route to accept it. Guidance waits for it: a steer that
   * reaches the server before the start has taken the thread's lease starts the run itself, and the
   * start is then refused, so the first instruction is the one that never runs.
   */
  private startInFlight: Promise<unknown> | null = null;

  constructor(private readonly transport: DurableAgentTransport, private readonly aiService: Pick<AIService, 'getThreadMessages'>, private readonly clock: DurableAdapterClock = DEFAULT_CLOCK) {}

  // ─── AgentChatAdapter surface ────────────────────────────────────────────────

  public get messages(): UIMessage[] {

    return this.state.messages;
  }

  public set messages(messages: UIMessage[]) {

    this.state.messages = messages;
    this.$contextGuardStatus.set(this.state.readContextGuardStatus());
  }

  public get status(): AgentChatStatus {

    return this.$status();
  }

  public get error(): Error | null {

    return this.$error();
  }

  public get contextGuardStatus(): AIChatContextGuardStatus | null {

    return this.$contextGuardStatus();
  }

  /**
   * Always false: the durable routes do not yet relay the typed provider-unavailable code, so a
   * provider refusal arrives as an ordinary run failure.
   */
  public get providerUnavailable(): boolean {

    return false;
  }

  /** Sends and card answers refused by the usage-limit door check, counted like the request-bound adapter counts them. */
  public get usageLimitRejections(): number {

    return this.$usageLimitRejections();
  }

  public clearProviderUnavailable(): void {

    // Nothing to clear; see `providerUnavailable`.
  }

  public get hasPendingApproval(): boolean {

    return this.state.pendingApprovalToolCallIds().length > 0;
  }

  public get lastMessage(): UIMessage | undefined {

    return this.state.lastMessage;
  }

  // ─── Durable-only surface ────────────────────────────────────────────────────

  /** Queued steering, in accepted order, until the loop echoes each entry. */
  public get pendingSteering(): AIDurablePendingSteeringEntry[] {

    return this.$pendingSteering();
  }

  /** Derived thread state from the last status read; null before the first read. */
  public get threadState(): AIDurableThreadState | null {

    return this.$threadState();
  }

  /** Decide-first copy while a card waits; the composer is disabled until approve, decline or stop. */
  public get decideFirstMessage(): string | null {

    return this.$decideFirstMessage();
  }

  /** Calm notice (temporarily unavailable, attached to another window's run, card expired). */
  public get notice(): string | null {

    return this.$notice();
  }

  /** Text Stop (or a failed run) handed back for the composer; cleared by `acknowledgeComposerRestore`. */
  public get composerRestore(): string | null {

    return this.$composerRestore();
  }

  /** The request the server refused, or null; Retry resends THIS and nothing else. */
  public get failedSend(): DurableFailedSend | null {

    return this.$failedSend();
  }

  /**
   * True while a send has left but the route has not answered it. Until then a refusal may still
   * hand words back, so the background pool keeps such an adapter rather than dropping it.
   */
  public get sendPending(): boolean {

    return this.$sendsOutstanding() > 0;
  }

  /** True while a refused send's file is still the composer's to keep; cleared by the acknowledgement. */
  public get attachmentsHeld(): boolean {

    return this.$heldRequestId() !== null;
  }

  /** The upload records of the send whose files are held for the composer; empty when none are. */
  public get heldUploads(): PendingFile[] {

    const requestId = this.$heldRequestId();
    return requestId ? this.uploadsFor(requestId) : [];
  }

  /** The chat service records which composer uploads went with a send, before handing it over. */
  public rememberUploads(requestId: string, files: PendingFile[]): void {

    if (files.length === 0) return;
    this.uploads.set(requestId, files);
    const oldest = this.uploads.keys().next().value;
    if (this.uploads.size > MAX_REMEMBERED_UPLOADS && oldest) this.uploads.delete(oldest);
  }

  /** The upload records that went with a send, for Retry to resend them. */
  public uploadsFor(requestId: string): PendingFile[] {

    return this.uploads.get(requestId) ?? [];
  }

  /** True while Stop is waiting for the server to confirm; the composer shows Stopping. */
  public get stopPending(): boolean {

    // The window shows a Stop as done the moment it is pressed; the server side settles unseen.
    return false;
  }

  public get attachedThreadId(): string | null {

    return this.threadId;
  }

  /** The composer has taken the refused request back; a later Retry has nothing of its own to send. */
  public clearFailedSend(): void {

    this.$failedSend.set(null);
  }

  /** The composer has put the held file back on screen. */
  public acknowledgeAttachmentsHeld(): void {

    this.$heldRequestId.set(null);
  }

  /** Dismisses the calm notice a refused route left (lease conflict, expired card); the chat container calls it on close. */
  public clearNotice(): void {

    this.$notice.set(null);
  }

  /**
   * The composer calls this once it has taken the words in `composerRestore` into its input, and only
   * then: it clears the signal, so acknowledging early would drop the words.
   */
  public acknowledgeComposerRestore(): void {

    this.$composerRestore.set(null);
  }

  /** Clears the error banner; the chat container calls it on dismiss or a fresh send. */
  public clearError(): void {

    this.$error.set(null);
  }

  /**
   * Forget the thread: closes the subscription and clears every per-thread signal, the replay guards
   * included.
   *
   * Only for leaving the thread: the chat service starting a new chat or dropping this adapter for a
   * pooled one, and `attach` moving to a different thread. A caller that may land back on the thread
   * this window is attached to must call `resetUnlessAttachedTo` instead; clearing the replay filter
   * and run bindings while staying makes the replay jms-ai sends every new subscriber render the live
   * reply a second time.
   */
  public resetTransientState(): void {

    this.dispose();
    this.toolInputBuffers.clear();
    this.replayFilter.clear();
    this.acceptedRunIds.clear();
    this.rejectedRunIds.clear();
    this.endedRunIds.clear();
    this.undecidedRunChunks.clear();
    this.undecidedRunReads.clear();
    this.foreignRunIds.clear();
    this.runBubbleIds.clear();
    this.stoppedSignalIds.clear();
    this.replayingPersistedStep.clear();
    this.lastChunkAtMs = 0;
    this.threadId = null;
    this.statusRead = null;
    this.pendingTurnUserMessageId = null;
    this.pendingTurnAssistantMessageId = null;
    this.stoppingRunIds.clear();
    this.stopSettling = false;
    this.reconnectAttempt = 0;
    this.unauthorizedRetried = false;
    this.replyCompleteRunId = null;
    this.stopTask = null;
    this.resolveStopSettled?.();
    this.resolveStopSettled = null;
    // A painted turn belongs to the thread being left; carried over, the next send would claim it.
    // (A send already in flight keeps its pair: it still renames the message when it lands.)
    this.paintedTurn = null;
    this.$status.set('idle');
    this.$error.set(null);
    this.$pendingSteering.set([]);
    this.$threadState.set(null);
    this.$decideFirstMessage.set(null);
    this.$notice.set(null);
    this.$failedSend.set(null);
    this.$heldRequestId.set(null);
    this.$stopPending.set(false);
    // Words handed back for this thread's composer would otherwise appear in the next thread's.
    this.$composerRestore.set(null);
  }

  /**
   * Forget the thread UNLESS this window is coming back to the one it is already watching. The chat
   * service calls it when it opens a thread, which can be the thread already attached.
   *
   * `resetTransientState` clears the guards that keep a run rendering ONCE: the replay filter, the
   * runs this window has accepted and sealed, and the reply bubble each run streams into. jms-ai
   * replays a live run from its first chunk to every new subscriber, so a window that wipes those
   * guards and then stays on the same thread reads that replay as a brand new run and opens a second
   * reply beside the one already on screen. The decision is made from the ATTACHED THREAD rather
   * than from whether the window looks busy, because a window can look idle while its run is alive —
   * a Stop the server refused, a card it settled locally, a silence poll on a status blip.
   */
  public resetUnlessAttachedTo(threadId: string | null): void {

    if (threadId !== null && this.threadId === threadId) return;
    this.resetTransientState();
  }

  /** Close the subscription and stop reconnecting; the run keeps executing on the server. */
  public dispose(): void {

    this.generation += 1;
    // Forgetting the thread is what fences the code still awaiting a response: start, steer, stop
    // and card answers check the thread after their await, and would otherwise re-arm a poll or a
    // stop check on an adapter nothing holds any more (after logout, a 401 every 30 s for ever).
    this.threadId = null;
    this.clearReconnectTimer();
    this.stopActivityPoll();
    this.clearStopVerification();
    this.clearCardExpiryCheck();
    this.clearRunEndRecheck();
    this.subscription?.close();
    this.subscription = null;
  }

  // ─── Attach ──────────────────────────────────────────────────────────────────

  /** Hydrate from the messages route, then read status and subscribe; resolves to the transcript now shown. */
  public async hydrateAndAttach(threadId: string): Promise<UIMessage[]> {

    const generation = this.generation;
    const hydration = ++this.hydrations;
    const messages = await this.readThreadMessages(threadId);
    // The window attached elsewhere, was disposed, or began a newer hydration while the read was out;
    // this transcript and an attach to its thread would overwrite the thread it moved to (or reopen a
    // subscription nothing holds). Overlapping hydrations share a generation, hence the count too.
    if (this.generation !== generation || this.hydrations !== hydration) return messages;
    if (this.threadId === threadId && this.believesRunning) {
      // The thread this window is already watching, mid-run: the live reply is ahead of storage, and
      // the replay filter and run bindings this window keeps drop the replay that could rebuild it.
      // Overwriting would delete it for good, so storage is spliced in around it instead.
      this.applyStoredMessages(messages, []);
    } else {
      this.messages = messages;
    }
    await this.attach(threadId);
    return this.messages;
  }

  /** Subscribe and read status together, on a thread whose messages the caller already hydrated. */
  public async attach(threadId: string): Promise<void> {

    if (this.threadId !== threadId) {
      this.resetTransientState();
      this.threadId = threadId;
    }
    this.clearReconnectTimer();
    // Timers armed for the previous generation would bail without re-arming, so the status read
    // below is what puts the silence watch and the card expiry check back on.
    this.stopActivityPoll();
    this.clearCardExpiryCheck();
    const generation = ++this.generation;
    // Both round trips leave TOGETHER. Subscribing only after the status read had answered put a
    // whole round trip in front of the run's first chunk, which is most of the blank second a window
    // sees when it opens a chat whose reply is already being written. Nothing is lost by starting
    // early: a chunk that arrives before the status result belongs to a run this window has not
    // decided on, so `holdUndecidedRun` parks it, and the first read to settle that run either
    // applies the held chunks or drops them as a finished run's replay.
    const statusRead = this.refreshStatus();
    // The cards the messages route restored, read BEFORE the subscription can raise a live one:
    // reconciliation below settles what storage handed back and never a card that has just arrived.
    const restoredCardIds = this.state.pendingApprovalToolCallIds();
    // Held, not shown, until the status read says they are really waiting. Storage keeps a card
    // marked pending until its run ENDS, so after an answer the stored copy still says "waiting"
    // while the run is already working on the reply; painting that card first showed the previous
    // round of questions on every reload. The holding state is the one the answering window shows
    // the moment it clicks, an in-progress tool row, which is true for a card that was answered and
    // a beat early for one that is still waiting.
    for (const toolCallId of restoredCardIds) this.state.patchToolPartEverywhere(toolCallId, { state: 'approval-responded' });
    this.openSubscription(generation);
    // A Stop this window never saw confirmed survives a detach, so its background check is re-armed
    // on the new generation rather than being left behind with the old subscription.
    if (this.$stopPending()) this.scheduleStopVerification();
    const status = await statusRead;
    if (generation !== this.generation) return;
    this.reconcileRestoredCards(status, restoredCardIds);
  }

  /**
   * Attach to a thread the caller created a moment ago: subscribe, and skip the status read.
   *
   * Nothing can be running on such a thread and no card can be waiting, so the read would decide
   * nothing. On a new chat it was the round trip standing between the press of Send and the reply
   * starting, after the thread creation the caller has already paid for.
   */
  private attachFresh(threadId: string): void {

    if (this.threadId !== threadId) {
      this.resetTransientState();
      this.threadId = threadId;
    }
    this.clearReconnectTimer();
    this.stopActivityPoll();
    this.clearCardExpiryCheck();
    const generation = ++this.generation;
    this.$threadState.set('idle');
    this.openSubscription(generation);
  }

  /**
   * A card restored from storage is only real while the server still holds it.
   *
   * The transcript can carry a tool part that was never settled — a card that expired, a run that
   * was stopped or died while it waited, or one that was answered and whose run has not ended yet
   * (storage only learns the answer at the end) — and the messages route hands every one of them
   * back as an actionable approval. Rendering that live replaces the composer with a card that is
   * either dead (its only possible answer a refusal) or already answered (the previous round of
   * questions, shown again). So `attach` holds every restored card as answered until this read
   * decides, and the status route is the authority: a card is live only when the thread is
   * `waiting` and the waiting run names this exact tool call. A running thread means the card was
   * answered and the hold stands; anything else settles as denied, expired when the reason says so.
   *
   * A status read that failed, or a waiting card whose tool calls the route did not name, disproves
   * nothing: the restored cards are shown exactly as storage had them.
   *
   * `restored` is the list of cards the transcript held when the attach began, not the list as it
   * stands now: the subscription is open while the status read is in flight, so a card the live run
   * raised in that gap is newer than the answer being applied here and is not this read's to settle.
   */
  private reconcileRestoredCards(status: AIDurableThreadStatusResponse | null, restoredAtAttach: string[]): void {

    // Only cards still in the hold are this read's to settle: one whose outcome arrived over the
    // subscription while the read was out is newer than the read, and must not become a card again.
    const restored = restoredAtAttach.filter(toolCallId => this.toolPartState(toolCallId) === 'approval-responded');
    if (restored.length === 0) return;
    const waiting = status?.state === 'waiting' && !isCardExpired({ state: status.state, suspendedAt: status.suspendedAt, expiresAt: status.expiresAt, nowMs: this.clock.now() });
    const liveToolCallIds = waiting ? status?.toolCallIds : undefined;
    if (!status || (waiting && !liveToolCallIds)) {
      for (const toolCallId of restored) this.state.patchToolPartEverywhere(toolCallId, { state: 'approval-requested' });
      return;
    }

    const expired = status.state === 'expired' || status.reason === 'expired' || (status.state === 'waiting' && !waiting);
    const answered = status.state === 'running';
    for (const toolCallId of restored) {
      if (liveToolCallIds?.includes(toolCallId)) {
        // Really waiting: the card comes out of the hold and takes the composer's place.
        this.state.patchToolPartEverywhere(toolCallId, { state: 'approval-requested' });
      } else if (!answered) {
        this.state.settleCard(toolCallId, expired);
      }
    }
    // The server still waits on a card this window cannot show, so the composer stays blocked with
    // an explanation rather than an overlay that could only be refused.
    this.$decideFirstMessage.set(waiting && !this.hasPendingApproval ? DECIDE_FIRST_NOTICE : null);
  }

  /** Counts an outcome landing on a card (a tool part raised for approval) as a card answer seen. */
  private noteCardOutcome(toolCallId: string): void {

    const isCard = this.state.messages.some(message => message.role === 'assistant' && (message.parts ?? []).some(part => AgentUtility.partField(part as AnyPart, 'toolCallId', '') === toolCallId && AgentUtility.partField(part as AnyPart, 'approval', undefined) !== undefined));
    if (isCard) this.cardAnswersSeen += 1;
  }

  /** The state of the newest tool part with this call id, or undefined when the transcript has none. */
  private toolPartState(toolCallId: string): string | undefined {

    for (const message of [...this.state.messages].reverse()) {
      if (message.role !== 'assistant') continue;
      const part = (message.parts ?? []).find(candidate => AgentUtility.partField(candidate as AnyPart, 'toolCallId', '') === toolCallId);
      if (part) return AgentUtility.partField<string>(part as AnyPart, 'state', '');
    }
    return undefined;
  }

  /**
   * Re-read status; heals queued cards, attaches to a run another window started, finalizes endings.
   * Concurrent callers share one read unless `fresh` is asked for, in which case a read that was
   * already in flight (and may predate what the caller just saw) is waited out and a new one issued.
   */
  public async refreshStatus(options: { fresh?: boolean } = {}): Promise<AIDurableThreadStatusResponse | null> {

    const threadId = this.threadId;
    if (!threadId) return null;
    if (this.statusRead) {
      // A read issued under an earlier attachment drops its result as stale, so a re-attach to the
      // same thread sharing it would get no status applied and no silence watch re-armed.
      if (!options.fresh && this.statusReadGeneration === this.generation) return this.statusRead;
      await this.statusRead;
      if (this.threadId !== threadId) return null;
    }
    const generation = this.generation;
    const runsAtRead = new Set(this.acceptedRunIds);
    const cardAnswersAtRead = this.cardAnswersSeen;
    this.statusReadGeneration = generation;
    this.statusRead = this.transport.status(threadId)
      .then(status => {
        // An outdated reading is withheld from callers too, not only from applyStatus: the silence
        // poll would otherwise finalize from it and seal the newer run it knows nothing of.
        if (this.isOutdatedReading(status, runsAtRead, cardAnswersAtRead)) return null;
        if (generation === this.generation && this.threadId === threadId) this.applyStatus(status);
        return status;
      })
      .catch((error: unknown) => {
        console.warn('[AgentDurable] status read failed:', toDurableRouteError(error).message);
        return null;
      })
      .finally(() => {
        this.statusRead = null;
      });
    return this.statusRead;
  }

  /**
   * A reading that describes the thread before something this window has since seen happen:
   * - quiet (idle, failed, expired) while a run accepted after the read still goes on: applying it
   *   would end that turn, clear its pending ids and stop its silence watch;
   * - waiting after a card answer landed since (accepted from here, or its outcome streamed in from
   *   another window's answer): applying it would put the window back to idle and show decide-first
   *   with no card left to answer, while the resumed run works on.
   */
  private isOutdatedReading(status: AIDurableThreadStatusResponse, runsAtRead: Set<string>, cardAnswersAtRead: number): boolean {

    if (status.state === 'waiting') return this.cardAnswersSeen !== cardAnswersAtRead;
    const quiet = status.state === 'idle' || status.state === 'failed' || status.state === 'expired';
    return quiet && [...this.acceptedRunIds].some(runId => !runsAtRead.has(runId) && !this.endedRunIds.has(runId));
  }

  // ─── Sending ─────────────────────────────────────────────────────────────────

  /**
   * Puts a turn on screen BEFORE the caller does the work the send needs.
   *
   * A brand new chat has to create its thread first — the durable send route only accepts a thread
   * that already exists — and that round trip is the one the user feels most, because the transcript
   * behind it is empty. The send claims this pair instead of painting its own; `abandonTurn` takes
   * it back off if the work fails and nothing is ever sent.
   */
  public beginTurn(userMessage: UIMessage): void {

    this.abandonTurn();
    this.paintedTurn = this.paintTurn(AgentMessageState.cloneMessage(userMessage));
  }

  /**
   * A message the chat service decided not to send after all (the user left the chat while it was
   * being created): its words go back to the composer like any refused send. Nothing is on screen
   * to remove, because leaving the chat already reset this adapter.
   */
  public handBackUnsent(message: UIMessage): void {

    this.recordFailedSend(message);
  }

  /**
   * Takes a painted turn back off the screen when it will never be sent.
   *
   * `restore: true` means the send was refused rather than superseded, so the words the composer
   * gave up go back to it and the request is remembered as the one Retry owns.
   */
  public abandonTurn(options: { restore?: boolean } = {}): void {

    if (!this.paintedTurn) return;
    if (options.restore) {
      const painted = this.state.messages.find(message => message.id === this.paintedTurn!.userMessageId);
      if (painted) this.recordFailedSend(painted);
    }
    this.state.removeMessages([this.paintedTurn.userMessageId, this.paintedTurn.placeholderId]);
    // The working state the paint put up goes with it; a window that looked idle before looks idle again.
    if (this.$status() === 'submitted') this.$status.set(this.paintedTurn.statusBefore);
    this.paintedTurn = null;
    this.sendInFlight = false;
  }

  /** The user's message and the reply bubble it is waiting for, on screen and owed a send. */
  private paintTurn(userMessage: UIMessage): PaintedTurn {

    const statusBefore = this.$status();
    this.state.appendUserMessage(userMessage);
    const placeholder = this.state.appendAssistantPlaceholder();
    // A turn is not visible to the server as a lease until the send route returns, so an `idle`
    // status read that lands in between says nothing about it — and must not read the thread as
    // finished and clear the pair that was just painted back off the screen.
    this.sendInFlight = true;
    // The working state goes up with the pair. The typing indicator under the empty bubble keys on
    // the adapter reporting the turn as submitted, and every round trip still ahead of the send
    // (thread creation, an attach, the send itself) is long enough for a bubble with no indicator
    // to read as broken. A window already watching a run keeps its streaming state: that send will
    // steer, and its intent is decided from the status recorded here, never from this flag.
    if (statusBefore === 'idle') this.$status.set('submitted');
    return { userMessageId: userMessage.id, placeholderId: placeholder.id, statusBefore };
  }

  /**
   * Sends a turn for the chat service: starts a run on an idle thread, or steers the run already going.
   *
   * The message is on screen before any round trip, and a send waits for a Stop still settling with
   * the server. Resolves once the send route has ACCEPTED the turn (or refused it), never when the
   * run finishes; the run's progress arrives over the subscription. A refusal never throws: the words
   * go back to the composer and `failedSend` holds the request for Retry.
   */
  public async sendTurn(request: AgentTurnRequest): Promise<void> {

    const threadId = request.threadId;
    if (!threadId) {
      // Nothing left this window, so the request is the composer's again rather than lost.
      this.abandonTurn({ restore: true });
      this.$error.set(new Error('This chat has no thread yet. Please try again.'));
      return;
    }
    this.clearError();
    this.$notice.set(null);
    this.$decideFirstMessage.set(null);
    // This send supersedes whatever the last refused one left behind.
    this.$failedSend.set(null);
    this.$heldRequestId.set(null);
    // New work on the thread: what an earlier Stop returned to the composer is no longer suppressed.
    this.stoppedSignalIds.clear();

    const userMessage = AgentMessageState.cloneMessage(request.userMessage);
    const text = this.extractText(userMessage);
    // The message goes on screen BEFORE any network work. Everything below is a round trip — the
    // attach or the status read, then the send itself — so painting afterwards left the user
    // watching an empty transcript for about a second after they pressed Send. A new chat has one
    // more round trip in front of all of it, the thread creation, so its caller paints through
    // `beginTurn` first; that pair is claimed here rather than a second copy standing beside it.
    if (this.paintedTurn && this.paintedTurn.userMessageId !== userMessage.id) this.abandonTurn();
    // A window that can see the agent working will steer: no pair is painted, since steering waits
    // as a queued bubble and gets its place in the transcript from the loop's echo.
    const steering = this.$status() === 'streaming' || this.$status() === 'submitted';
    const painted = this.paintedTurn ?? (steering ? { userMessageId: userMessage.id, placeholderId: '', statusBefore: this.$status() } : this.paintTurn(userMessage));
    this.paintedTurn = null;

    const stopsBefore = this.stopPresses;
    try {
      // A Stop the window already showed may still be settling with the server; the message is on
      // screen, and it leaves once the stopped run has let go of the thread.
      if (this.stopTask) {
        const attachedThreadId = this.threadId;
        const attachedGeneration = this.generation;
        await this.stopTask;
        if (this.supersededWhileWaiting(attachedThreadId, attachedGeneration, userMessage, painted)) return;
      }
      if (request.threadIsNew) {
        this.attachFresh(threadId);
      } else if (this.threadId !== threadId) {
        // The attach resets the adapter for the new thread before its first await, which returns the
        // status to idle; the pair is still on screen and still owed its send, so the working state
        // is put back at once rather than after the status round trip the attach then waits out.
        const attaching = this.attach(threadId);
        const attachedGeneration = this.generation;
        this.restorePaintedStatus(painted);
        await attaching;
        if (this.supersededWhileWaiting(threadId, attachedGeneration, userMessage, painted)) return;
      } else if (this.$threadState() === null) {
        const attachedGeneration = this.generation;
        await this.refreshStatus();
        if (this.supersededWhileWaiting(threadId, attachedGeneration, userMessage, painted)) return;
      }
      // Stop pressed while the send was still preparing: nothing has left, so nothing may now.
      if (this.stopPresses !== stopsBefore) {
        this.handBackStoppedSend(userMessage, text, painted);
        return;
      }
      this.restorePaintedStatus(painted);

      const context: AIAgentClientContext = {
        interactionMode: request.interactionMode,
        modelTier: request.modelTier,
        currentJobId: request.currentJobId,
        currentWorkOrderId: request.currentWorkOrderId,
        attachmentBlobUrls: request.attachmentBlobUrls,
        attachedFiles: this.extractAttachedFiles(userMessage, request.attachmentBlobUrls),
      };
      // Decided from the status the window had BEFORE the paint: the paint itself moved an idle
      // window to submitted, which is this send, not a run to steer.
      const intent = resolveSendIntent(painted.statusBefore, this.$threadState());
      if (intent === 'steer') {
        // Steering has no reply bubble of its own: the text joins the queue and only enters the
        // transcript when the running loop echoes it, so a pair painted before the attach found the
        // thread running comes back off first.
        if (painted.placeholderId) this.state.removeMessages([userMessage.id, painted.placeholderId]);
        // A file prepared while this window looked idle cannot ride a steer — the run only ever
        // receives the text — so the send is refused whole rather than delivering half of it.
        if (this.hasFileParts(userMessage)) {
          this.refuseAttachmentDuringRun(userMessage.id, text);
          return;
        }
        if (this.startInFlight) {
          const attachedGeneration = this.generation;
          await this.startInFlight;
          if (this.supersededWhileWaiting(threadId, attachedGeneration, userMessage, painted)) return;
          if (this.stopPresses !== stopsBefore) {
            this.handBackStoppedSend(userMessage, text, painted);
            return;
          }
        }
        await this.steer(threadId, userMessage.id, text, context);
        return;
      }
      await this.start(threadId, userMessage, text, painted.placeholderId, context);
    } finally {
      this.sendInFlight = false;
    }
  }

  /**
   * True, with the send's own pair taken back off, when the window moved to another thread (or was
   * reset) while the send waited to leave. Sending then would re-attach the thread the user left and
   * reset the one they opened, so the unsent request is dropped instead.
   */
  private supersededWhileWaiting(threadId: string | null, generation: number, userMessage: UIMessage, painted: PaintedTurn): boolean {

    if (this.threadId === threadId && this.generation === generation) return false;
    this.state.removeMessages([userMessage.id, painted.placeholderId].filter(Boolean));
    return true;
  }

  /** A send Stop caught before it left: its pair comes off and its words go back, as Stop does with guidance. */
  private handBackStoppedSend(userMessage: UIMessage, text: string, painted: PaintedTurn): void {

    this.state.removeMessages([userMessage.id, painted.placeholderId].filter(Boolean));
    this.restoreToComposer([text]);
    if (this.hasFileParts(userMessage)) this.$heldRequestId.set(userMessage.id);
  }

  /**
   * The working state a paint put up, back on a window whose attach or status read returned it to
   * idle. A read that found the thread running or waiting is left as it is: that send will steer,
   * or be refused with decide-first, and either way the paint is not what the window shows.
   */
  private restorePaintedStatus(painted: PaintedTurn): void {

    if (painted.statusBefore === 'idle' && this.$status() === 'idle') this.$status.set('submitted');
  }

  private async start(threadId: string, userMessage: UIMessage, text: string, placeholderId: string, context: AIAgentClientContext): Promise<void> {

    // The pair is already on screen; this claims it, so a refused send still rolls both back.
    this.pendingTurnUserMessageId = userMessage.id;
    this.pendingTurnAssistantMessageId = placeholderId;
    this.$status.set('submitted');
    const generation = this.generation;
    const stopsBefore = this.stopPresses;

    let response: AIDurableSendResponse;
    const sending = this.trackSend(this.transport.send(threadId, { ...context, message: text, intent: 'start' }));
    const accepted = sending.catch(() => undefined);
    this.startInFlight = accepted;
    void accepted.finally(() => {
      if (this.startInFlight === accepted) this.startInFlight = null;
    });
    try {
      response = await sending;
    } catch (error) {
      if (this.attachedElsewhere(threadId, generation)) {
        // The pending turn, status and composer now belong to another thread; only this send's own pair may go.
        this.state.removeMessages([userMessage.id, placeholderId].filter(Boolean));
        return;
      }
      this.rollbackPendingTurn();
      this.$status.set('idle');
      // The pair came off the screen, so the request lives only here now: back to the composer, and
      // remembered so Retry resends THIS request rather than the previous, already answered one.
      this.recordFailedSend(userMessage);
      this.handleRouteError(toDurableRouteError(error), 'send');
      return;
    }
    const started = response.mode === 'started';
    // Re-key the optimistic bubble BEFORE the thread-identity check below. The rename is by message
    // id, so it lands on the right message wherever that message now lives — and if the window moved
    // to another thread mid-send, skipping it would leave the message stranded under a local id that
    // can never be matched against storage again, which is how a sent message ends up on screen twice.
    if (started) this.state.renameMessage(userMessage.id, response.messageId);
    if (this.threadId !== threadId) return;
    if (started && this.stopPresses !== stopsBefore) {
      this.stopRunStartedAfterStop(threadId, response.runId);
      return;
    }
    if (started) {
      this.acceptRun(response.runId);
      this.startActivityPoll();
      this.pendingTurnUserMessageId = response.messageId;
      return;
    }
    // The lease said a run was already going; the text was delivered as steering instead. The pair
    // comes off BEFORE the run is accepted, so chunks held for it open their own reply bubble rather
    // than streaming into the placeholder that is about to go.
    this.rollbackPendingTurn();
    this.acceptRun(response.runId);
    this.startActivityPoll();
    this.$status.set('streaming');
    this.enqueueSteering(response.messageId, text);
    // Steering carries text only, so a file on this message never left the browser. It stays in the
    // composer with an explanation rather than disappearing with the turn that was just rolled back.
    if (this.hasFileParts(userMessage)) this.holdAttachments(userMessage.id, ATTACHMENT_NOT_SENT_NOTICE);
  }

  private async steer(threadId: string, requestId: string, text: string, context: AIAgentClientContext): Promise<void> {

    const generation = this.generation;
    const stopsBefore = this.stopPresses;
    let response: AIDurableSendResponse;
    try {
      response = await this.trackSend(this.transport.send(threadId, { ...context, message: text, intent: 'steer' }));
    } catch (error) {
      // Handing the words back now would put them in another thread's composer.
      if (this.attachedElsewhere(threadId, generation)) return;
      // Steering paints nothing, so a refusal leaves the words nowhere at all unless they go back.
      this.recordFailedSteer(requestId, text);
      this.handleRouteError(toDurableRouteError(error), 'send');
      return;
    }
    if (this.threadId !== threadId) return;
    if (response.mode === 'steered') {
      this.acceptRun(response.runId);
      this.startActivityPoll();
      this.enqueueSteering(response.messageId, text);
      return;
    }
    // The run had just finished: the text became the user message of a fresh run. The message goes
    // on screen BEFORE the run is accepted, so chunks held for it stream into the reply bubble under
    // the question rather than into one opened above it.
    this.paintStartedSteer(response.runId, { id: response.messageId, role: 'user', parts: [{ type: 'text', text }] } as UIMessage);
    // Stop was pressed while this steer was out: the run it started is stopped like a start's would
    // be, and the message stays on screen as a stopped turn, which is how storage holds it.
    if (this.stopPresses !== stopsBefore) {
      this.stopRunStartedAfterStop(threadId, response.runId);
      return;
    }
    this.acceptRun(response.runId);
    this.startActivityPoll();
    if (this.$status() === 'idle') this.$status.set('submitted');
  }

  /**
   * The user message of a run a steer started, placed ahead of that run's reply. A status read may
   * already have accepted the run and opened its bubble while the send was out; the question then
   * goes in front of that bubble instead of after the reply it asked for.
   */
  private paintStartedSteer(runId: string, userMessage: UIMessage): void {

    const firstBubbleId = this.runBubbleIds.get(runId)?.[0];
    const bubbleIndex = firstBubbleId ? this.state.messages.findIndex(message => message.id === firstBubbleId) : -1;
    if (bubbleIndex !== -1) {
      const messages = [...this.state.messages];
      messages.splice(bubbleIndex, 0, userMessage);
      this.state.messages = messages;
      this.pendingTurnUserMessageId = userMessage.id;
      this.pendingTurnAssistantMessageId = firstBubbleId ?? null;
      return;
    }
    this.state.appendUserMessage(userMessage);
    const placeholder = this.state.appendAssistantPlaceholder();
    this.pendingTurnUserMessageId = userMessage.id;
    this.pendingTurnAssistantMessageId = placeholder.id;
  }

  /** Counts a send as outstanding until the route answers it, either way. */
  private trackSend<T>(sending: Promise<T>): Promise<T> {

    this.$sendsOutstanding.update(count => count + 1);
    return sending.finally(() => this.$sendsOutstanding.update(count => count - 1));
  }

  /** True once the window has moved to another thread (or re-attached) since a send left for this one. */
  private attachedElsewhere(threadId: string, generation: number): boolean {

    return this.threadId !== threadId || this.generation !== generation;
  }

  private enqueueSteering(signalId: string, text: string): void {

    // The loop can drain and echo the guidance before the send response returns; it is in the
    // transcript already, and queueing it again would hand it back to the composer on Stop. A Stop
    // confirmed before the response arrived has already handed it back, and no run will consume it.
    if (this.stoppedSignalIds.has(signalId)) return;
    if (this.state.messages.some(message => message.id === signalId)) return;
    this.$pendingSteering.update(entries => entries.some(entry => entry.signalId === signalId)
      ? entries
      : [...entries, { signalId, text, acceptedAt: new Date(this.clock.now()).toISOString() }]);
  }

  /**
   * Takes the turn's pair off the screen. A placeholder that already holds content stays: another
   * window's run, accepted while this send was out, streamed its reply into it.
   */
  private rollbackPendingTurn(): void {

    const placeholder = this.state.messages.find(message => message.id === this.pendingTurnAssistantMessageId);
    const placeholderId = (placeholder?.parts?.length ?? 0) > 0 ? null : this.pendingTurnAssistantMessageId;
    const ids = [this.pendingTurnUserMessageId, placeholderId].filter((id): id is string => Boolean(id));
    this.state.removeMessages(ids);
    this.pendingTurnUserMessageId = null;
    this.pendingTurnAssistantMessageId = null;
  }

  /**
   * Keep a refused request instead of dropping it.
   *
   * The text goes back to the composer, where the user left it, and the request itself is kept so
   * Retry can only resend this one. A file on it never left either: it is held for the composer to
   * put back (the files were cleared from the composer the moment Send was pressed), and Retry sends
   * it again with the text. Everything the thread already holds is untouched: the rollback above
   * removes the refused pair and nothing else.
   */
  private recordFailedSend(message: UIMessage): void {

    const text = this.extractText(message);
    this.$failedSend.set({ requestId: message.id, text });
    this.restoreToComposer([text]);
    if (this.hasFileParts(message)) this.$heldRequestId.set(message.id);
  }

  /** Steering carries no files and paints nothing, so its refusal hands back only the words. */
  private recordFailedSteer(requestId: string, text: string): void {

    this.$failedSend.set({ requestId, text });
    this.restoreToComposer([text]);
  }

  /** Whether the message carries a file the durable steering path could not deliver. */
  private hasFileParts(message: UIMessage): boolean {

    return (message.parts ?? []).some(part => part.type === 'file');
  }

  /**
   * A send with a file arrived at a thread that is already running. Refuse it whole and say so: the
   * text returns to the composer and the file stays attached for the caller to put back, so nothing
   * is quietly dropped and no half of the message reaches the run as text-only guidance.
   */
  private refuseAttachmentDuringRun(requestId: string, text: string): void {

    this.holdAttachments(requestId, ATTACHMENT_REFUSED_NOTICE);
    this.restoreToComposer([text]);
  }

  /** The file did not travel: the composer keeps it, and the window says why. */
  private holdAttachments(requestId: string, notice: string): void {

    this.$heldRequestId.set(requestId);
    this.$notice.set(notice);
  }

  // ─── Stop ────────────────────────────────────────────────────────────────────

  /**
   * Ends all work on the thread. The window shows it stopped at once: the reply is marked Stopped,
   * unfinished tool steps settle, open cards close and queued guidance returns to the composer. The
   * server side follows in the background, where a tool already running is allowed to finish (a
   * tool cut mid-call can leave half-written data), and storage is read back once it confirms so a
   * tool that did finish shows its real result. Repeated presses share the one stop.
   */
  public stop(): Promise<void> {

    const threadId = this.threadId;
    if (!threadId) return Promise.resolve();
    this.stopPresses += 1;
    if (this.stopTask) return this.stopTask;
    if (this.$stopPending()) return Promise.resolve();
    // The reply is already complete and the run is only saving it: a stop now would mark a finished
    // reply as stopped. Its `finish` ends the turn on its own in a moment.
    if (this.replyCompleteRunId && this.acceptedRunIds.has(this.replyCompleteRunId) && !this.endedRunIds.has(this.replyCompleteRunId)) return Promise.resolve();
    const live = [...this.acceptedRunIds].filter(runId => !this.endedRunIds.has(runId));
    const superseded = this.sealLiveRuns();
    // A run accepted but not drawn yet is over too: none of its later chunks may paint.
    for (const runId of live) this.endedRunIds.add(runId);
    const waitingCardIds = this.state.pendingApprovalToolCallIds();
    this.settleStop(this.$pendingSteering());
    const last = this.state.lastMessage;
    const stampedMessageId = last?.role === 'assistant' && !readRunCompletion(last) ? last.id : null;
    this.finishEnding('stopped', 'stop-requested');
    // Only this stop clears itself: a failed one already cleared it, and a later stop may hold the slot by then.
    const task: Promise<void> = this.settleStopWithServer(threadId, superseded, live, { stampedMessageId, waitingCardIds }).finally(() => {
      if (this.stopTask === task) this.stopTask = null;
    });
    this.stopTask = task;
    return task;
  }

  /**
   * Stop was pressed while this window's start, or a steer that ended up starting a run, was still
   * out, and the send won anyway: the stop route found the thread idle before the send took its
   * lease. The window already shows the turn stopped, so the run is sealed before any chunk of it can
   * draw, and stopped again on the server, so no agent work goes on after the user said stop.
   */
  private stopRunStartedAfterStop(threadId: string, runId: string): void {

    // Sealed without being accepted, so chunks already parked for it are dropped rather than drawn.
    this.undecidedRunChunks.delete(runId);
    this.sealRun(runId);
    this.replayFilter.forgetRun(runId);
    const last = this.state.lastMessage;
    const stampedMessageId = last?.role === 'assistant' && !readRunCompletion(last) ? last.id : null;
    this.finishEnding('stopped', 'stop-requested');
    // This stop holds the stop slot like the click's own: a send waits for it, or the thread-wide
    // stop could end the run that send starts. It joins a stop still settling rather than replace it.
    const settling = Promise.all([this.stopTask, this.stopRunWithServer(threadId, runId, stampedMessageId)]).then(() => undefined);
    const task: Promise<void> = settling.finally(() => {
      if (this.stopTask === task) this.stopTask = null;
    });
    this.stopTask = task;
  }

  /** The server half of stopping a run that started after Stop; settles once the stop is confirmed or given up on. */
  private async stopRunWithServer(threadId: string, runId: string, stampedMessageId: string | null): Promise<void> {

    const generation = this.generation;
    let result: AIDurableStopResponse;
    try {
      result = await this.transport.stop(threadId);
    } catch (error) {
      console.warn('[AgentDurable] stopping a run that started after Stop failed:', toDurableRouteError(error).message);
      if (this.attachedElsewhere(threadId, generation)) return;
      // The stop slot is let go first, so the status read that resumes the run is not held back by it.
      this.stopTask = null;
      await this.resumeWatchingUnstoppedRun(runId, stampedMessageId);
      return;
    }
    if (this.threadId !== threadId || result.stopped) return;
    // Unconfirmed: the window keeps checking, and a send keeps waiting, until the run has let go.
    await new Promise<void>(resolve => {
      this.resolveStopSettled = resolve;
      this.holdStopping(result);
    });
  }

  /**
   * The second stop never reached the server, so nothing says the run it was aimed at stopped. As
   * with any unconfirmed stop, the window stops claiming it: the run is watched again (its ending
   * re-read from storage, since the chunks dropped meanwhile are gone), the Stopped ending comes off,
   * the notice says so, and a status read brings back the working state if the run is still going.
   */
  private async resumeWatchingUnstoppedRun(runId: string, stampedMessageId: string | null): Promise<void> {

    this.endedRunIds.delete(runId);
    this.acceptRun(runId, { foreign: true });
    this.removeRunCompletion(stampedMessageId);
    this.$notice.set(STOP_UNCONFIRMED_NOTICE);
    await this.refreshStatus({ fresh: true });
  }

  /** The server half of a Stop the window already shows as done. */
  private async settleStopWithServer(threadId: string, superseded: string[], stoppedRunIds: string[], optimistic: { stampedMessageId: string | null; waitingCardIds: string[] }): Promise<void> {

    let result: AIDurableStopResponse;
    try {
      result = await this.transport.stop(threadId);
    } catch (error) {
      if (this.threadId !== threadId) return;
      // Nothing confirms the stop, so the window must not go on claiming it: the runs it sealed may
      // draw again, the notice says so, and a status read shows the run if it is in fact still going.
      // Chunks that arrived while they were sealed are gone, so their ending is read back from
      // storage (as for a run another window started) rather than trusted to what was drawn.
      for (const runId of stoppedRunIds) {
        this.endedRunIds.delete(runId);
        this.foreignRunIds.add(runId);
      }
      // The reply goes on, so the Stopped ending the click put on it comes off; an ending already
      // on a message is never replaced, and the real one would otherwise be lost under it.
      this.removeRunCompletion(optimistic.stampedMessageId);
      this.stopTask = null;
      this.$notice.set(STOP_UNCONFIRMED_NOTICE);
      console.warn('[AgentDurable] stop was not confirmed:', toDurableRouteError(error).message);
      const status = await this.refreshStatus({ fresh: true });
      if (this.threadId === threadId) this.restoreStillWaitingCards(status, optimistic.waitingCardIds);
      return;
    }
    if (this.threadId !== threadId) return;
    if (!result.stopped) {
      // The stop route ran out of its confirmation window, usually because a tool is still finishing;
      // that is a deadline, not a termination. The run has been told to stop, so it starts no further
      // step and the guidance already in the composer is safe. The window keeps showing it stopped,
      // checks in the background, and this task (which a send waits on) lasts until it settles.
      await new Promise<void>(resolve => {
        this.resolveStopSettled = resolve;
        this.holdStopping(result);
      });
      if (this.threadId !== threadId) return;
      await this.readBackAfterStop(threadId, superseded);
      return;
    }
    // Anything the window already handed back is not handed back twice (see discardSteering).
    this.settleStop(result.unconsumed);
    await this.refreshStatus({ fresh: true });
    await this.readBackAfterStop(threadId, superseded);
  }

  /**
   * Storage is the truth once the run has ended: a tool the stop let finish shows its result instead
   * of the cancelled step the window drew. Skipped once a new turn is under way, which owns the
   * transcript from then on.
   */
  private async readBackAfterStop(threadId: string, superseded: string[]): Promise<void> {

    const generation = this.generation;
    try {
      const messages = await this.readThreadMessages(threadId);
      if (generation !== this.generation || this.threadId !== threadId) return;
      if (this.$status() !== 'idle' || this.paintedTurn || this.sendInFlight) return;
      // Stop sealed every run it was aimed at, so an accepted run still unsealed is newer, such as one
      // another window started that now waits on a card. Its turn is not this stop's to mark stopped.
      if ([...this.acceptedRunIds].some(runId => !this.endedRunIds.has(runId))) return;
      this.applyStoredMessages(messages, superseded);
      this.finishEnding('stopped', 'stop-requested');
    } catch (error) {
      console.warn('[AgentDurable] read-back after a stop failed:', toDurableRouteError(error).message);
    }
  }

  /**
   * Puts back the cards the click settled when the stop never reached the server and the run still
   * waits on them. The approval event is not replayed to a window that already drew it, so without
   * this the live card stayed declined and could only be answered after a reload.
   */
  private restoreStillWaitingCards(status: AIDurableThreadStatusResponse | null, cardIds: string[]): void {

    if (cardIds.length === 0 || status?.state !== 'waiting') return;
    if (isCardExpired({ state: status.state, suspendedAt: status.suspendedAt, expiresAt: status.expiresAt, nowMs: this.clock.now() })) return;
    const waiting = cardIds.filter(toolCallId => status.toolCallIds?.includes(toolCallId) ?? true);
    for (const toolCallId of waiting) this.state.patchToolPartEverywhere(toolCallId, { state: 'approval-requested' });
    if (waiting.length > 0) this.$decideFirstMessage.set(null);
  }

  /** Takes the run ending off one message, leaving the rest of its metadata alone. */
  private removeRunCompletion(messageId: string | null): void {

    if (!messageId) return;
    this.state.messages = this.state.messages.map(message => {
      if (message.id !== messageId || !isRecord(message.metadata)) return message;
      const metadata = { ...message.metadata };
      delete metadata[AI_DURABLE_RUN_COMPLETION_METADATA_KEY];
      return { ...message, metadata } as UIMessage;
    });
  }

  /** An unconfirmed stop: remember what it was aimed at, say Stopping, and start checking. */
  private holdStopping(result: AIDurableStopResponse): void {

    for (const runId of [...result.abortedRunIds, ...result.cancelledSuspendedRunIds]) this.stoppingRunIds.add(runId);
    this.$stopPending.set(true);
    this.clearStopVerification();
    this.scheduleStopVerification();
  }

  /** Everything a CONFIRMED stop retires: queued guidance, open cards, the silence watch, the composer. */
  private settleStop(unconsumed: AIDurablePendingSteeringEntry[]): void {

    this.clearStopVerification();
    this.discardSteering(unconsumed);
    this.$decideFirstMessage.set(null);
    this.state.settlePendingApprovals(false);
    this.state.settleUnfinishedToolParts();
    this.stopActivityPoll();
    this.$status.set('idle');
    this.$threadState.set('idle');
  }

  /** Arms the next background check; harmless to call repeatedly, since a live timer is never replaced. */
  private scheduleStopVerification(): void {

    if (this.stopVerifyTimer !== null || !this.threadId) return;
    const generation = this.generation;
    const delay = Math.min(DURABLE_STOP_VERIFY_BASE_MS * 2 ** this.stopVerifyAttempt, DURABLE_ACTIVE_POLL_INTERVAL_MS);
    this.stopVerifyAttempt += 1;
    this.stopVerifyTimer = this.clock.setTimeout(() => {
      this.stopVerifyTimer = null;
      void this.verifyStopped(generation);
    }, delay);
  }

  /**
   * Ask the server whether the run really ended.
   *
   * Only the server ends the Stopping state. A status read that failed proves nothing — a lost
   * connection is not a stopped run — so it simply asks again, as it does while the run this Stop
   * targeted still holds the thread. A different run holding it means the targeted one is gone and
   * someone started new work, which is left alone. A quiet thread (idle, failed or expired) is
   * settled by `applyStatus` as the reading is applied, so it is not settled a second time here.
   */
  private async verifyStopped(generation: number): Promise<void> {

    if (generation !== this.generation || !this.$stopPending()) return;
    const status = await this.refreshStatus({ fresh: true });
    if (generation !== this.generation || !this.$stopPending()) return;
    const working = status?.state === 'running' || status?.state === 'waiting';
    if (working && status?.runId && !this.stoppingRunIds.has(status.runId)) {
      this.endStopping();
      return;
    }
    // A settle in flight asks again itself if it cannot confirm.
    if (!this.stopSettling) this.scheduleStopVerification();
  }

  /**
   * The thread reads quiet: stop once more. On a quiet thread the stop route confirms at once and
   * does what the first call could not — removes guidance the loop drained without a turn and hands
   * back everything unconsumed, for every window — so only now does the guidance come back. A stop
   * that is still unconfirmed (new work appeared and was stopped with it) keeps the window Stopping.
   */
  private async settleUnconfirmedStop(generation: number): Promise<void> {

    const threadId = this.threadId;
    if (!threadId || this.stopSettling || !this.$stopPending()) return;
    this.stopSettling = true;
    let result: AIDurableStopResponse | null = null;
    try {
      result = await this.transport.stop(threadId);
    } catch (error) {
      console.warn('[AgentDurable] settling an unconfirmed stop failed:', toDurableRouteError(error).message);
    } finally {
      this.stopSettling = false;
    }
    if (generation !== this.generation || this.threadId !== threadId || !this.$stopPending()) return;
    if (!result) {
      this.scheduleStopVerification();
      return;
    }
    if (!result.stopped) {
      this.holdStopping(result);
      return;
    }
    // Anything a status read already handed back (a run finalized as failed) is not handed back twice.
    this.endStopping();
    this.settleStop(result.unconsumed.filter(entry => !entry.signalId || !this.stoppedSignalIds.has(entry.signalId)));
    // A turn painted behind this stop keeps its typing indicator; it sends once the stop task resolves.
    if (this.sendInFlight) this.$status.set('submitted');
    await this.refreshStatus({ fresh: true });
  }

  /** The Stopping state is over, however it ended. */
  private endStopping(): void {

    this.$stopPending.set(false);
    this.stoppingRunIds.clear();
    this.clearStopVerification();
    this.resolveStopSettled?.();
    this.resolveStopSettled = null;
  }

  private clearStopVerification(): void {

    if (this.stopVerifyTimer !== null) {
      this.clock.clearTimeout(this.stopVerifyTimer);
      this.stopVerifyTimer = null;
    }
    this.stopVerifyAttempt = 0;
  }

  /**
   * Steering handed back (by Stop, or by a status read after a crashed run) belongs in the composer
   * and nowhere else: it leaves the queued stack, and an entry the loop had already echoed leaves the
   * transcript too (it sits there under its signal id, and the user is about to see the same words in
   * the composer). The ids are remembered so an older status read cannot queue them again.
   */
  private discardSteering(entries: AIDurablePendingSteeringEntry[]): void {

    // A message already handed back (an earlier stop reply, a status read) never goes into the composer twice.
    const fresh = entries.filter(entry => !entry.signalId || !this.stoppedSignalIds.has(entry.signalId));
    this.restoreToComposer(fresh.map(entry => entry.text));
    const signalIds = entries.map(entry => entry.signalId).filter(Boolean);
    for (const signalId of signalIds) this.stoppedSignalIds.add(signalId);
    this.state.removeMessages(signalIds);
    this.$pendingSteering.set([]);
  }

  private restoreToComposer(texts: string[]): void {

    const restored = texts.map(text => text.trim()).filter(Boolean).join('\n\n');
    if (!restored) return;
    this.$composerRestore.update(current => (current ? `${current}\n\n${restored}` : restored));
  }

  // ─── Cards ───────────────────────────────────────────────────────────────────

  /**
   * Approves a waiting card for the chat service, carrying the interview answers when the card is one.
   * Resolves true once the server has accepted the answer and resumed the run, not when the run ends;
   * false when it was refused, with the card put back (or settled as expired) and a notice shown.
   */
  public async approveToolCall(toolCallId: string, interviewResponse?: AIInterviewResponse): Promise<boolean> {

    return this.answerCard(toolCallId, () => this.transport.approve(this.threadId ?? '', { toolCallId, ...(interviewResponse ? { interviewResponse } : {}) }));
  }

  /**
   * Declines a waiting card for the chat service, with the user's reason when one was given. Same
   * guarantee as `approveToolCall`: true means the server accepted the decline and resumed the run.
   */
  public async declineToolCall(toolCallId: string, reason?: string): Promise<boolean> {

    return this.answerCard(toolCallId, () => this.transport.decline(this.threadId ?? '', { toolCallId, ...(reason ? { reason } : {}) }), reason);
  }

  private async answerCard(toolCallId: string, call: () => Promise<{ runId: string }>, declineReason?: string): Promise<boolean> {

    const threadId = this.threadId;
    if (!threadId) return false;
    this.clearError();
    this.$notice.set(null);
    this.state.updateToolPart(toolCallId, { state: 'approval-responded' });
    this.$status.set('streaming');
    const generation = this.generation;
    const stopsBefore = this.stopPresses;
    try {
      const result = await call();
      if (this.attachedElsewhere(threadId, generation)) return true;
      this.cardAnswersSeen += 1;
      // Stop already settled the card and the run, and its own server call ends the resumed run;
      // marking the thread running again would make the next send steer into a run that is gone.
      if (this.stopPresses !== stopsBefore) return true;
      this.acceptRun(result.runId);
      this.$threadState.set('running');
      this.$decideFirstMessage.set(null);
      this.clearCardExpiryCheck();
      this.startActivityPoll();
      // A decline the resumed loop reports without a tool-output-denied chunk still lands calmly.
      if (declineReason && !this.state.hasOutputForToolCall(toolCallId)) {
        this.state.patchToolPartEverywhere(toolCallId, { state: 'output-denied', errorText: '' });
      }
      return true;
    } catch (error) {
      // The card belongs to the thread the window has since left; its refusal must not touch the new one.
      if (this.attachedElsewhere(threadId, generation)) return false;
      // Stop ended the run while the answer was out, so a late refusal must not reopen the card. Stop
      // leaves a card held as answered alone; refused, it was never answered, and settles as denied.
      if (this.stopPresses !== stopsBefore) {
        if (this.toolPartState(toolCallId) === 'approval-responded') this.state.settleCard(toolCallId, false);
        return false;
      }
      const routeError = toDurableRouteError(error);
      if (routeError.code === 'NO_WAITING_CARD') {
        await this.reconcileUnwaitedCard(toolCallId);
        return false;
      }
      this.$status.set('idle');
      if (routeError.code === 'CARD_EXPIRED') {
        // The card is held as answered by now, so the pending sweep in the error handler skips it.
        this.state.settleCard(toolCallId, true);
        this.handleRouteError(routeError, 'card');
        return false;
      }
      this.state.updateToolPart(toolCallId, { state: 'approval-requested' });
      this.handleRouteError(routeError, 'card');
      return false;
    }
  }

  /**
   * The server holds no card for this answer: another window answered it, or the run was stopped or
   * ended there. Reopening the card would offer an answer that can only be refused again, so the
   * window keeps it held and lets a status read say what happened. An outcome that already arrived
   * over the subscription is the answer, and is left exactly as it landed.
   */
  private async reconcileUnwaitedCard(toolCallId: string): Promise<void> {

    if (this.toolPartState(toolCallId) !== 'approval-responded') return;
    this.$status.set('idle');
    const generation = this.generation;
    const status = await this.refreshStatus({ fresh: true });
    if (generation !== this.generation || !status) return;
    // Running: another window's answer resumed the run, and the outcome follows over the subscription.
    if (status.state === 'running') return;
    if (this.toolPartState(toolCallId) !== 'approval-responded') return;
    this.state.settleCard(toolCallId, status.state === 'expired' || status.reason === 'expired');
  }

  /** When the waiting card expires: the time the status route computed, else its suspension plus the card window. */
  private cardExpiryMs(status: AIDurableThreadStatusResponse): number {

    if (status.expiresAt) return Date.parse(status.expiresAt);
    if (status.suspendedAt) return Date.parse(status.suspendedAt) + AGENT_CARD_EXPIRY_MS;
    return this.clock.now() + AGENT_CARD_EXPIRY_MS;
  }

  /**
   * Arms the one status read that retires a card left open past its expiry. Nothing else asks while
   * a thread waits — the run is parked, so no chunk arrives and the silence watch is off — and the
   * card otherwise stayed actionable, with the composer hidden behind it, until a reload. The read
   * lands just after the expiry, so the browser's own expiry check agrees with the server's.
   */
  private armCardExpiryCheck(expiresAtMs: number): void {

    this.clearCardExpiryCheck();
    if (!this.threadId || !Number.isFinite(expiresAtMs)) return;
    const generation = this.generation;
    const delay = Math.max(expiresAtMs - this.clock.now(), 0) + CARD_EXPIRY_CHECK_MARGIN_MS;
    this.cardExpiryTimer = this.clock.setTimeout(() => {
      this.cardExpiryTimer = null;
      if (generation !== this.generation) return;
      void this.refreshStatus({ fresh: true });
    }, delay);
  }

  private clearCardExpiryCheck(): void {

    if (this.cardExpiryTimer === null) return;
    this.clock.clearTimeout(this.cardExpiryTimer);
    this.cardExpiryTimer = null;
  }

  // ─── Status ──────────────────────────────────────────────────────────────────

  private applyStatus(status: AIDurableThreadStatusResponse): void {

    // Guidance a crashed run never answered goes back to the composer, and its echo leaves the
    // transcript: the server deleted that row, so keeping it would show the words twice. Handed back
    // before the queue is rebuilt, so a lagging read cannot queue them again.
    if (status.unconsumedSteering?.length) this.discardSteering(status.unconsumedSteering);
    // A read that started before the loop echoed an entry still lists it as queued; the echo is newer.
    const echoedIds = new Set(this.state.messages.map(message => message.id));
    this.$pendingSteering.set((status.pendingSteering ?? []).filter(entry => !this.stoppedSignalIds.has(entry.signalId) && !echoedIds.has(entry.signalId)));
    this.$threadState.set(status.state);

    switch (status.state) {
      case 'running':
        // A run this window has seen end is finished here: either the one it showed as stopped, only
        // finishing its last tool (a send waits for it to let go of the thread), or a snapshot taken
        // before it ended. Neither brings the working state back or opens a reply bubble for it.
        if (status.runId && this.endedRunIds.has(status.runId)) break;
        // The thread is working, whoever started it: Stop shows, Send steers, and the subscription
        // is watched for silence. This is the whole of what a reload needs to rejoin a live run.
        this.$decideFirstMessage.set(null);
        this.clearCardExpiryCheck();
        if (status.runId) this.acceptRun(status.runId, { foreign: !this.acceptedRunIds.has(status.runId) });
        if (this.$status() === 'idle') this.$status.set('streaming');
        // The reply is being written but storage holds none of it yet, so opening the thread would
        // otherwise show the question with a blank answer under it until the first chunk lands. This
        // is the same empty bubble `paintTurn` puts up when the send starts here, so a run joined
        // from another window waits behind the same working state as one this window started.
        if (status.runId) this.ensureLivePlaceholder(status.runId);
        this.startActivityPoll();
        break;
      case 'waiting':
        // A card raised elsewhere (or before a reload) belongs to a run whose stored reply must
        // replace whatever its replay streams here once it ends, exactly like a running foreign run.
        if (status.runId) this.acceptRun(status.runId, { foreign: !this.acceptedRunIds.has(status.runId) });
        this.$status.set('idle');
        if (isCardExpired({ state: status.state, suspendedAt: status.suspendedAt, expiresAt: status.expiresAt, nowMs: this.clock.now() })) {
          this.clearCardExpiryCheck();
          this.state.settlePendingApprovals(true);
          this.$decideFirstMessage.set(null);
          break;
        }
        this.armCardExpiryCheck(this.cardExpiryMs(status));
        // The card waits on the server but this window holds no card to answer it with.
        if (!this.hasPendingApproval) this.$decideFirstMessage.set(DECIDE_FIRST_NOTICE);
        break;
      case 'expired':
        this.state.settlePendingApprovals(true);
        this.finishEnding('expired', 'card-expired');
        break;
      case 'failed':
        this.state.settlePendingApprovals(false);
        this.state.settleUnfinishedToolParts();
        // The run died before this window saw any reply from it, so its ending has no message to
        // sit under here. Storage now holds an empty "didn't finish" message for it; the same empty
        // message goes up locally so the ending shows under this turn, never under the one before.
        if (this.state.lastMessage?.role === 'user') this.state.appendAssistantPlaceholder();
        this.finishEnding('failed', 'orphaned');
        break;
      case 'idle':
        // A turn this window just sent is not visible as a lease until the send route returns; an
        // idle reading in that window says nothing about it. That window opens the moment the user
        // presses Send, which is when the optimistic pair goes on screen — hence `sendInFlight` as
        // well as the `submitted` the send sets once it is away.
        if (this.$status() === 'submitted' || this.sendInFlight) break;
        this.finishEnding(status.reason ?? 'completed', status.reason ? 'status' : 'completed');
        break;
    }
    // A Stop still waiting for confirmation settles the moment a fresh status read finds the thread
    // quiet, whether a background check or the run's own ending prompted the read. Only a status
    // read triggers it, never a later step acting on an older one, so one quiet reading means one
    // settling stop. A turn painted since does not hold it back: that send waits for this very stop,
    // so it cannot have started a run of its own yet, and holding the settle left it waiting for ever.
    const quiet = status.state === 'idle' || status.state === 'failed' || status.state === 'expired';
    if (this.$stopPending() && quiet) {
      void this.settleUnconfirmedStop(this.generation);
    }
  }

  /** Marks the ending on the final assistant message and returns the composer to idle. */
  private finishEnding(reason: AIDurableRunCompletionReason, finishReason: string): void {

    this.$status.set('idle');
    this.stopActivityPoll();
    this.$decideFirstMessage.set(null);
    this.clearCardExpiryCheck();
    const last = this.state.lastMessage;
    if (last?.role === 'assistant' && !readRunCompletion(last)) {
      if (reason === 'completed' && (last.parts?.length ?? 0) === 0) {
        this.state.removeEmptyTrailingAssistant();
      } else if (reason !== 'completed') {
        this.state.decorateLastAssistant(AI_DURABLE_RUN_COMPLETION_METADATA_KEY, buildRunCompletion(reason, finishReason, this.clock.now()));
        this.state.settlePendingApprovals(reason === 'expired');
        this.state.settleUnfinishedToolParts();
      }
    }
    this.pendingTurnUserMessageId = null;
    this.pendingTurnAssistantMessageId = null;
  }

  // ─── Silence watch ───────────────────────────────────────────────────────────

  /** Whether this window believes the thread is producing work right now. */
  private get believesRunning(): boolean {

    return this.$status() === 'streaming' || this.$status() === 'submitted' || this.$threadState() === 'running';
  }

  /** Arms one silence check; harmless to call repeatedly, since a live timer is never replaced. */
  private startActivityPoll(): void {

    if (this.activityPollTimer !== null || !this.threadId) return;
    const generation = this.generation;
    this.activityPollTimer = this.clock.setTimeout(() => {
      this.activityPollTimer = null;
      void this.checkForSilentRun(generation);
    }, DURABLE_ACTIVE_POLL_INTERVAL_MS);
  }

  private stopActivityPoll(): void {

    if (this.activityPollTimer === null) return;
    this.clock.clearTimeout(this.activityPollTimer);
    this.activityPollTimer = null;
  }

  /**
   * The interval elapsed. Chunks arriving means the subscription is healthy and nothing is asked of
   * the status route; a genuinely silent run is checked, and a route that answers idle, failed or
   * expired ends the turn here from storage, because no terminal chunk is coming.
   */
  private async checkForSilentRun(generation: number): Promise<void> {

    if (generation !== this.generation || !this.threadId || !this.believesRunning) return;
    if (this.clock.now() - this.lastChunkAtMs < DURABLE_ACTIVE_POLL_INTERVAL_MS) {
      this.startActivityPoll();
      return;
    }
    const status = await this.refreshStatus({ fresh: true });
    if (generation !== this.generation) return;
    if (status && (status.state === 'idle' || status.state === 'failed' || status.state === 'expired')) {
      await this.finalizeFromStorage(status);
      return;
    }
    this.startActivityPoll();
  }

  /**
   * Ends the local turn from what storage holds. The bubbles the silent run streamed into are
   * superseded by the stored reply, so the transcript never carries the partial beside the final,
   * and the ending is re-marked in case the runtime never wrote one.
   */
  private async finalizeFromStorage(status: AIDurableThreadStatusResponse): Promise<void> {

    const threadId = this.threadId;
    const generation = this.generation;
    if (!threadId) return;
    this.stopActivityPoll();
    const superseded = this.sealLiveRuns();
    const knownRunIds = new Set(this.acceptedRunIds);
    try {
      const messages = await this.readThreadMessages(threadId);
      if (generation !== this.generation || this.threadId !== threadId) return;
      // A turn sent during the read owns the transcript now; this older read would remove its reply
      // bubble and end it. Same thread, same generation, so only the turn itself can tell.
      const newerRun = [...this.acceptedRunIds].some(runId => !knownRunIds.has(runId) && !this.endedRunIds.has(runId));
      if (this.paintedTurn || this.sendInFlight || newerRun) return;
      this.applyStoredMessages(messages, superseded);
      this.finishEnding(status.reason ?? 'completed', status.reason ? 'status' : 'completed');
    } catch (error) {
      console.warn('[AgentDurable] read-back after a silent run failed:', toDurableRouteError(error).message);
    }
  }

  // ─── Subscription ────────────────────────────────────────────────────────────

  private openSubscription(generation: number): void {

    const threadId = this.threadId;
    if (!threadId || generation !== this.generation) return;
    this.subscription?.close();
    let connectedAtMs: number | null = null;
    const handle = this.transport.subscribe(threadId, event => {
      connectedAtMs ??= this.clock.now();
      if (generation === this.generation) this.handleEvent(event);
    }, this.replayFilter);
    this.subscription = handle;
    void handle.ended.then(end => {
      if (generation !== this.generation || this.subscription !== handle) return;
      this.subscription = null;
      if (end.reason === 'closed') return;
      // A quiet thread sends nothing after the preamble, so a connection that stayed up for a while
      // has proven itself; one that dropped straight after opening keeps counting towards the cap.
      if (connectedAtMs !== null && this.clock.now() - connectedAtMs >= DURABLE_HEALTHY_CONNECTION_MS) this.reconnectAttempt = 0;
      const unauthorizedFirstTime = end.reason === 'error' && end.error.status === 401 && !this.unauthorizedRetried;
      if (unauthorizedFirstTime) this.unauthorizedRetried = true;
      if (end.reason === 'error' && PERMANENT_SUBSCRIBE_STATUSES.has(end.error.status) && !unauthorizedFirstTime) {
        this.$notice.set(end.error.message);
        return;
      }
      this.scheduleReconnect(generation);
    });
  }

  /** Reconnect with backoff; the run is never aborted by a dropped connection. */
  private scheduleReconnect(generation: number): void {

    this.clearReconnectTimer();
    if (this.reconnectAttempt >= DURABLE_RECONNECT_MAX_ATTEMPTS) {
      this.$notice.set(CONNECTION_LOST_NOTICE);
      return;
    }
    const delay = Math.min(DURABLE_RECONNECT_BASE_MS * 2 ** this.reconnectAttempt, DURABLE_RECONNECT_MAX_MS);
    this.reconnectAttempt += 1;
    this.reconnectTimer = this.clock.setTimeout(() => {
      this.reconnectTimer = null;
      if (generation !== this.generation) return;
      void this.refreshStatus().then(() => {
        if (generation !== this.generation) return;
        this.openSubscription(generation);
      });
    }, delay);
  }

  private clearReconnectTimer(): void {

    if (this.reconnectTimer !== null) {
      this.clock.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private handleEvent(event: DurableSubscriptionEvent): void {

    if (!isRecord(event.chunk)) return;
    const chunk = event.chunk;
    const runId = readString(chunk, 'runId');
    // A run this window watched end is finished work: every later chunk of it is the replay a fresh
    // subscription receives, and applying it would stand a second copy of the reply on screen.
    if (runId && this.endedRunIds.has(runId)) return;
    if (runId && this.rejectedRunIds.has(runId)) return;
    // While a Stop settles, a run the window had not accepted is the one being stopped (or its
    // replay): nothing of it is drawn after the window already showed the stop.
    if (runId && this.stopTask && !this.acceptedRunIds.has(runId)) {
      this.rejectedRunIds.add(runId);
      return;
    }
    if (runId && !this.acceptedRunIds.has(runId)) {
      this.holdUndecidedRun(runId, event);
      return;
    }
    // Any event proves the token was accepted, but only run content proves the connection healthy:
    // the preamble opens every subscription, including one that drops a moment later.
    this.unauthorizedRetried = false;
    if (readString(chunk, 'type') !== SUBSCRIPTION_PREAMBLE_TYPE) this.reconnectAttempt = 0;
    this.lastChunkAtMs = this.clock.now();
    this.applyChunk(chunk, runId);
  }

  /**
   * A chunk for a run this window did not start: replayed history or a run another window started.
   * Hold it until one status read says whether that run is live.
   */
  private holdUndecidedRun(runId: string, event: DurableSubscriptionEvent): void {

    const held = this.undecidedRunChunks.get(runId) ?? [];
    held.push(event);
    this.undecidedRunChunks.set(runId, held);
    if (this.undecidedRunReads.has(runId)) return;
    this.undecidedRunReads.add(runId);
    // A read issued before this chunk arrived cannot say whether the run behind it is live.
    void this.refreshStatus({ fresh: true }).then(status => {
      // A read that failed decides nothing. Dropping the run here would leave the window idle while
      // the agent kept working, so a later chunk of this run asks again instead — and the chunks
      // stay parked so the retry can still apply them. It waits a moment first: chunks arrive many
      // times a second, and asking on each one while the status route is down is a request storm.
      if (!status) {
        this.clock.setTimeout(() => this.undecidedRunReads.delete(runId), UNDECIDED_RUN_RETRY_MS);
        return;
      }
      this.undecidedRunReads.delete(runId);
      // Another read got there first (the one `attach` issues alongside the subscription, or the
      // send that started this very run); it released the held chunks already.
      if (this.acceptedRunIds.has(runId)) return;
      const live = (status.state === 'running' || status.state === 'waiting') && status.runId === runId;
      if (!live) {
        this.undecidedRunChunks.delete(runId);
        this.rejectedRunIds.add(runId);
        this.replayFilter.forgetRun(runId);
        void this.readBackRejectedRun();
        return;
      }
      this.acceptRun(runId, { foreign: true });
    });
  }

  /**
   * A run this window never watched had already ended by the time a status read asked about it. It
   * may be one another window started a moment ago, whose question and answer are now in storage and
   * nowhere on this screen, so storage is read back rather than leaving them out until a reload. Its
   * replayed chunks stay dropped; the stored copy is the only one drawn.
   */
  private async readBackRejectedRun(): Promise<void> {

    const threadId = this.threadId;
    const generation = this.generation;
    if (!threadId) return;
    try {
      const messages = await this.readThreadMessages(threadId);
      if (generation !== this.generation || this.threadId !== threadId) return;
      // A turn this window has under way owns the transcript; the next read-back catches up.
      if (this.$status() !== 'idle' || this.paintedTurn || this.sendInFlight) return;
      this.applyStoredMessages(messages, []);
    } catch (error) {
      console.warn('[AgentDurable] read-back after an unwatched run failed:', toDurableRouteError(error).message);
    }
  }

  private acceptRun(runId: string, options: { foreign?: boolean } = {}): void {

    this.rejectedRunIds.delete(runId);
    this.acceptedRunIds.add(runId);
    if (options.foreign) this.foreignRunIds.add(runId);
    this.releaseHeldChunks(runId);
  }

  /**
   * Chunks parked while the run was undecided, applied in arrival order now that it is accepted.
   *
   * Whichever status read settles the run releases them, so a chunk that beat the attach's read does
   * not have to wait out a second read of its own before it can be seen.
   */
  private releaseHeldChunks(runId: string): void {

    const held = this.undecidedRunChunks.get(runId);
    if (!held) return;
    this.undecidedRunChunks.delete(runId);
    this.lastChunkAtMs = this.clock.now();
    for (const event of held) {
      if (isRecord(event.chunk)) this.applyChunk(event.chunk, runId);
    }
  }

  private applyChunk(chunk: Record<string, unknown>, runId: string | undefined): void {

    const type = readString(chunk, 'type') ?? '';
    const payload = isRecord(chunk['payload']) ? chunk['payload'] : undefined;
    if (runId && !this.admitReplayedChunk(runId, type, payload)) return;
    if (this.completeEarlierToolCall(runId, type, chunk, payload)) return;
    if (RUN_CONTENT_CHUNK_TYPES.has(type)) this.beginRunActivity(runId);

    switch (type) {
      case SUBSCRIPTION_PREAMBLE_TYPE: {
        // The run holding the thread lease when this subscription opened is live by definition:
        // its replay is applied at once instead of parked behind the status read. A run the
        // preamble does not name is decided by status as before, since one may start right after.
        const liveRunId = readString(chunk, 'liveRunId');
        if (liveRunId && !this.endedRunIds.has(liveRunId) && !this.acceptedRunIds.has(liveRunId)) this.acceptRun(liveRunId, { foreign: true });
        break;
      }
      case 'start':
        this.$decideFirstMessage.set(null);
        this.clearError();
        break;
      case 'step-start':
        // Another step after a "last" one (steering woven in at the boundary): the run is working again.
        if (runId && runId === this.replyCompleteRunId) this.replyCompleteRunId = null;
        this.adoptStoredMessageId(runId, readString(payload, 'messageId'));
        break;
      case 'step-finish': {
        this.adoptStoredMessageId(runId, readString(payload, 'messageId'));
        // The last step is over (it did not end in a tool call): the reply is complete, and the run
        // only saves and tidies up before its `finish`, which can take a few seconds.
        const stepResult = isRecord(payload?.['stepResult']) ? payload['stepResult'] : undefined;
        const reason = readString(stepResult, 'reason') ?? readString(payload, 'finishReason');
        if (runId && reason && reason !== 'tool-calls') this.replyCompleteRunId = runId;
        break;
      }
      case 'text-start':
        this.state.startTextPart();
        break;
      case 'text-delta':
        this.state.appendTextDelta(readString(payload, 'text') ?? '');
        break;
      case 'reasoning-start':
        this.state.startReasoningPart();
        break;
      case 'reasoning-delta':
        this.state.appendReasoningDelta(readString(payload, 'text') ?? '');
        break;
      case 'tool-call-input-streaming-start': {
        const toolCallId = readString(payload, 'toolCallId');
        if (!toolCallId) break;
        this.toolInputBuffers.set(toolCallId, '');
        this.state.updateToolPart(toolCallId, { toolName: readString(payload, 'toolName'), state: 'input-streaming', input: {} });
        break;
      }
      case 'tool-call-delta': {
        const toolCallId = readString(payload, 'toolCallId');
        if (!toolCallId) break;
        const buffer = `${this.toolInputBuffers.get(toolCallId) ?? ''}${readString(payload, 'argsTextDelta') ?? ''}`;
        this.toolInputBuffers.set(toolCallId, buffer);
        this.state.updateToolPart(toolCallId, { toolName: readString(payload, 'toolName'), state: 'input-streaming', input: buffer });
        break;
      }
      case 'tool-call-input-streaming-end': {
        const toolCallId = readString(payload, 'toolCallId');
        if (!toolCallId) break;
        const buffer = this.toolInputBuffers.get(toolCallId) ?? '';
        this.toolInputBuffers.delete(toolCallId);
        this.state.updateToolPart(toolCallId, { state: 'input-available', input: this.parseToolInput(buffer) });
        break;
      }
      case 'tool-call': {
        const toolCallId = readString(payload, 'toolCallId');
        if (!toolCallId) break;
        this.state.updateToolPart(toolCallId, { toolName: readString(payload, 'toolName'), state: 'input-available', input: payload?.['args'] });
        break;
      }
      case 'tool-call-approval': {
        const toolCallId = readString(payload, 'toolCallId');
        if (!toolCallId) break;
        const approvalRunId = runId ?? readString(payload, 'runId');
        this.state.updateToolPart(toolCallId, {
          toolName: readString(payload, 'toolName'),
          state: 'approval-requested',
          input: payload?.['args'],
          approval: { id: toolCallId, ...(approvalRunId ? { runId: approvalRunId } : {}) },
        });
        // A status read names the exact expiry and replaces this estimate; a replayed approval never
        // pushes back a check the status read already armed.
        if (this.cardExpiryTimer === null) this.armCardExpiryCheck(this.clock.now() + AGENT_CARD_EXPIRY_MS);
        this.$threadState.set('waiting');
        this.$decideFirstMessage.set(null);
        this.$status.set('idle');
        break;
      }
      case 'tool-output-denied': {
        const toolCallId = readString(chunk, 'toolCallId') ?? readString(payload, 'toolCallId');
        if (!toolCallId) break;
        this.noteCardOutcome(toolCallId);
        const toolName = readString(chunk, 'toolName') ?? readString(payload, 'toolName');
        this.toolInputBuffers.delete(toolCallId);
        this.state.updateToolPart(toolCallId, { ...(toolName ? { toolName } : {}), state: 'output-denied' });
        break;
      }
      case 'tool-result': {
        const toolCallId = readString(payload, 'toolCallId');
        if (!toolCallId) break;
        this.noteCardOutcome(toolCallId);
        this.toolInputBuffers.delete(toolCallId);
        const isError = payload?.['isError'] === true;
        this.state.updateToolPart(toolCallId, {
          toolName: readString(payload, 'toolName'),
          state: isError ? 'output-error' : 'output-available',
          output: payload?.['result'],
          ...(isError ? { errorText: this.extractErrorMessage(payload?.['result']) } : {}),
        });
        break;
      }
      case 'tool-error': {
        const toolCallId = readString(payload, 'toolCallId');
        if (!toolCallId) break;
        this.noteCardOutcome(toolCallId);
        this.toolInputBuffers.delete(toolCallId);
        this.state.updateToolPart(toolCallId, { toolName: readString(payload, 'toolName'), state: 'output-error', errorText: this.extractErrorMessage(payload?.['error'] ?? 'Tool execution failed') });
        break;
      }
      case 'data-user-message': {
        const data = isRecord(chunk['data']) ? chunk['data'] : undefined;
        const signalId = readString(data, 'id');
        if (!signalId) break;
        this.$pendingSteering.update(entries => entries.filter(entry => entry.signalId !== signalId));
        const text = signalContentsText(data?.['contents']);
        this.state.insertEchoedUserMessage({ id: signalId, role: 'user', parts: [{ type: 'text', text }], createdAt: new Date(this.clock.now()) } as UIMessage);
        break;
      }
      case 'data-context-status':
        if (isAIChatContextGuardStatus(chunk['data'])) this.applyContextGuardStatus(chunk['data']);
        break;
      case 'error': {
        const code = readString(payload, 'code');
        if (code === AI_CHAT_CONTEXT_LIMIT_REACHED_CODE && isAIChatContextGuardStatus(payload?.['contextStatus'])) {
          this.rollbackPendingTurn();
          this.applyContextGuardStatus(payload['contextStatus']);
          this.clearError();
        } else {
          this.$error.set(new Error(readString(payload, 'message') || 'Stream error'));
        }
        this.onRunEnded(runId);
        break;
      }
      case 'abort':
      // A processor ended the run, such as the AI usage mid-run guard at the spend cutoff. No
      // `finish` follows it; the host re-reads the limit status once the window stops working.
      case 'tripwire':
        this.onRunEnded(runId);
        break;
      case 'finish': {
        const stepResult = isRecord(payload?.['stepResult']) ? payload['stepResult'] : undefined;
        const reason = readString(stepResult, 'reason') ?? readString(payload, 'finishReason');
        if (reason !== 'tool-calls') this.onRunEnded(runId);
        break;
      }
      default:
        break;
    }
  }

  /**
   * Whether a chunk of this run may be applied, given what the transcript already holds.
   *
   * A subscription replays a run from its first chunk. A run that suspended on a card persisted
   * everything up to that card, and a newer Mastra may persist every step as it finishes, so the
   * hydrated transcript can already hold the very message a replayed `step-start` names. Applying
   * that step again streams its reasoning, text and tool calls onto the stored message a second
   * time. So a step whose message the transcript holds with content is skipped whole: every chunk
   * of the run is dropped until the next `step-start` (or the run's end) says which message the run
   * is writing now. The run's own live bubble is never mistaken for a stored step, since it is bound
   * to the run here.
   */
  private admitReplayedChunk(runId: string, type: string, payload: Record<string, unknown> | undefined): boolean {

    if (type === 'step-start') {
      const messageId = readString(payload, 'messageId');
      if (messageId && this.isPersistedStep(runId, messageId)) {
        this.replayingPersistedStep.add(runId);
        this.dropUnneededPlaceholder();
        return false;
      }
      this.replayingPersistedStep.delete(runId);
      return true;
    }
    if (!this.replayingPersistedStep.has(runId)) return true;
    // The one thing a persisted step can still be missing is the outcome its card received: the run
    // resumed with it, but storage only learns it when the run ends. It lands on the stored part.
    if (type === 'tool-result' || type === 'tool-output-denied' || type === 'tool-error') {
      this.completeStoredToolCall(type, payload);
      return false;
    }
    const stepResult = isRecord(payload?.['stepResult']) ? payload['stepResult'] : undefined;
    const ended = type === 'abort' || type === 'error' || type === 'tripwire' || (type === 'finish' && (readString(stepResult, 'reason') ?? readString(payload, 'finishReason')) !== 'tool-calls');
    // The run's ending is applied even mid-skip, so the window learns the run is over.
    if (ended) this.replayingPersistedStep.delete(runId);
    return ended;
  }

  /**
   * A live tool outcome for a call that sits in a message other than the run's live reply bubble,
   * such as a card restored from storage and answered in another window: it lands on that part where
   * it is. Treated as an ordinary chunk it would open a new reply bubble, draw the outcome there as a
   * second copy, and leave the card showing as waiting. The run is working again all the same, so
   * the window shows it working.
   */
  private completeEarlierToolCall(runId: string | undefined, type: string, chunk: Record<string, unknown>, payload: Record<string, unknown> | undefined): boolean {

    if (type !== 'tool-result' && type !== 'tool-output-denied' && type !== 'tool-error') return false;
    const toolCallId = readString(payload, 'toolCallId') ?? readString(chunk, 'toolCallId');
    if (!toolCallId || !this.state.hasToolPart(toolCallId)) return false;
    const liveBubbleId = runId ? this.currentBubbleId(runId) : undefined;
    const liveBubble = this.state.messages.find(message => message.id === liveBubbleId);
    if (liveBubble?.parts.some(part => AgentUtility.partField(part as AnyPart, 'toolCallId', '') === toolCallId)) return false;
    this.completeStoredToolCall(type, { ...payload, toolCallId });
    if (this.$status() !== 'streaming') this.$status.set('streaming');
    if (this.$threadState() !== 'running') this.$threadState.set('running');
    this.startActivityPoll();
    return true;
  }

  /** Applies a replayed tool outcome to the stored part that already holds the call, wherever it sits. */
  private completeStoredToolCall(type: string, payload: Record<string, unknown> | undefined): void {

    const toolCallId = readString(payload, 'toolCallId');
    if (!toolCallId || !this.state.hasToolPart(toolCallId)) return;
    this.noteCardOutcome(toolCallId);
    this.toolInputBuffers.delete(toolCallId);
    if (type === 'tool-output-denied') {
      this.state.patchToolPartEverywhere(toolCallId, { state: 'output-denied', errorText: '' });
      return;
    }
    if (type === 'tool-error') {
      this.state.patchToolPartEverywhere(toolCallId, { state: 'output-error', errorText: this.extractErrorMessage(payload?.['error'] ?? 'Tool execution failed') });
      return;
    }
    const isError = payload?.['isError'] === true;
    this.state.patchToolPartEverywhere(toolCallId, {
      state: isError ? 'output-error' : 'output-available',
      output: payload?.['result'],
      ...(isError ? { errorText: this.extractErrorMessage(payload?.['result']) } : {}),
    });
  }

  /** True when the transcript holds this message with content and it is not the run's own live bubble. */
  private isPersistedStep(runId: string, messageId: string): boolean {

    if (this.runBubbleIds.get(runId)?.includes(messageId)) return false;
    // A per-step message may have been joined into an earlier one at read time; it counts as held.
    const message = this.state.messages.find(candidate => candidate.id === messageId || AgentUtility.mergedMessageIds(candidate).includes(messageId));
    return !!message && (message.parts?.length ?? 0) > 0;
  }

  /**
   * An empty bubble opened for a replay that turned out to be a stored step has nothing to receive.
   * A bubble painted for this window's own pending turn is left alone.
   */
  private dropUnneededPlaceholder(): void {

    const last = this.state.lastMessage;
    if (!last || last.role !== 'assistant' || (last.parts?.length ?? 0) > 0) return;
    if (last.id === this.pendingTurnAssistantMessageId || last.id === this.paintedTurn?.placeholderId) return;
    this.state.removeEmptyTrailingAssistant();
  }

  /**
   * A content chunk means this run is working in this window right now, whether it is the run this
   * window started, one another window started, or the replay a reload receives mid-run. Binding it
   * to its reply bubble and showing the active state here is what lets a reloaded page rejoin a run
   * without waiting for a fresh `start` it will never see.
   */
  private beginRunActivity(runId: string | undefined): void {

    this.ensureLivePlaceholder(runId);
    if (this.$status() !== 'streaming') this.$status.set('streaming');
    if (this.$threadState() !== 'running') this.$threadState.set('running');
    this.startActivityPoll();
  }

  /**
   * Every run streams into ONE reply bubble. A turn sent from this window already has its
   * placeholder; so does an empty trailing bubble left by a steering echo. Anything else (a run that
   * started elsewhere, a replay arriving after real history) opens a bubble here, and a run already
   * bound to the trailing bubble reuses it, so a replayed `start` never opens a second reply.
   */
  private ensureLivePlaceholder(runId: string | undefined): void {

    const last = this.state.lastMessage;
    if (runId && this.currentBubbleId(runId) === last?.id) return;
    if (last?.role === 'assistant' && (last.parts?.length ?? 0) === 0) {
      this.bindBubble(runId, last.id);
      return;
    }
    const placeholder = this.state.appendAssistantPlaceholder();
    this.bindBubble(runId, placeholder.id);
  }

  /** The reply bubble a run is streaming into right now, or undefined before it opened one. */
  private currentBubbleId(runId: string): string | undefined {

    const bubbles = this.runBubbleIds.get(runId);
    return bubbles?.[bubbles.length - 1];
  }

  private bindBubble(runId: string | undefined, messageId: string): void {

    if (!runId) return;
    const bubbles = this.runBubbleIds.get(runId) ?? [];
    if (bubbles[bubbles.length - 1] === messageId) return;
    bubbles.push(messageId);
    this.runBubbleIds.set(runId, bubbles);
  }

  /**
   * Mastra names the assistant message each step will persist. Adopting that id makes the bubble
   * this window is streaming into the SAME message storage will hold, so the read-back at the end of
   * the run replaces it instead of standing a second copy of the same reply beside it.
   *
   * Only an empty bubble is renamed: re-keying a message the user is already reading would rebuild
   * it in the DOM, and a bubble that has content keeps its local id, which the superseded list
   * covers just as well.
   */
  private adoptStoredMessageId(runId: string | undefined, messageId: string | undefined): void {

    if (!runId || !messageId) return;
    const bubbles = this.runBubbleIds.get(runId);
    const current = bubbles?.[bubbles.length - 1];
    if (!bubbles || !current || current === messageId) return;
    const live = this.state.lastMessage;
    if (live?.id !== current || (live.parts?.length ?? 0) > 0) return;
    this.state.renameMessage(current, messageId);
    bubbles[bubbles.length - 1] = messageId;
    if (this.pendingTurnAssistantMessageId === current) this.pendingTurnAssistantMessageId = messageId;
  }

  /**
   * Marks a run finished and returns the bubbles it wrote into, for the storage read-back to
   * supersede. The binding itself is kept: a replayed chunk of this run must find the bubble it
   * already has rather than open another, even though the seal means none should arrive.
   */
  private sealRun(runId: string): string[] {

    this.endedRunIds.add(runId);
    this.foreignRunIds.delete(runId);
    return this.runBubbleIds.get(runId) ?? [];
  }

  /**
   * Seals every run this window was still following and returns their bubbles; used by Stop and when a
   * silent run is finalized from storage. Runs that already ended keep their bindings for replays, but
   * their bubbles are history: superseding them would let the newest-page read-back erase older
   * replies on a long chat.
   */
  private sealLiveRuns(): string[] {

    const bubbles: string[] = [];
    for (const [runId, runBubbles] of this.runBubbleIds) {
      if (this.endedRunIds.has(runId)) continue;
      this.endedRunIds.add(runId);
      bubbles.push(...runBubbles);
    }
    this.foreignRunIds.clear();
    this.toolInputBuffers.clear();
    return bubbles;
  }

  /** Takes the transcript from storage and keeps the context measurement in step with it. */
  private applyStoredMessages(messages: UIMessage[], supersededIds: string[]): void {

    this.state.reconcileFromStorage(messages, supersededIds);
    this.$contextGuardStatus.set(this.state.readContextGuardStatus());
  }

  /**
   * The live stream ended. The run is sealed first, so the replay every later subscription receives
   * is dropped instead of rendered again. The reason lives in storage, so read status to write it
   * onto the final message; a run another window started (which includes the run a reload rejoined)
   * is re-read whole, and the bubbles its replay streamed into are superseded by what storage holds,
   * so the finished reply replaces the replayed one instead of appearing twice.
   */
  private onRunEnded(runId: string | undefined): void {

    this.$status.set('idle');
    this.stopActivityPoll();
    const foreign = runId ? this.foreignRunIds.has(runId) : false;
    const superseded = runId ? this.sealRun(runId) : [];
    if (runId) {
      // Safe only because the seal above drops this run's chunks first: the event ids are released
      // for memory, never so the run can be applied a second time.
      this.replayFilter.forgetRun(runId);
      this.replayingPersistedStep.delete(runId);
      this.toolInputBuffers.clear();
    }
    this.clearRunEndRecheck();
    this.confirmRunEnded({ foreign, superseded, attempt: 0 });
  }

  /**
   * Reads status until it no longer reports the ended run as working, then re-reads a foreign run's
   * reply from storage.
   *
   * The status route keeps reporting a finished run as running for a second or two while its final
   * writes settle, and a read can fail. Stopping after one such read left the thread state `running`
   * with nothing left to correct it, so the next send carrying a file was refused as steering. The
   * reads back off and give up after a few seconds. The sealed run never reopens its reply bubble:
   * `applyStatus` ignores a running reading of a run this window has seen end.
   */
  private confirmRunEnded(ending: { foreign: boolean; superseded: string[]; attempt: number }): void {

    const threadId = this.threadId;
    const generation = this.generation;
    if (!threadId) return;
    void this.refreshStatus({ fresh: true }).then(async status => {
      if (generation !== this.generation) return;
      if (this.isEndedRunStillReported(status) && ending.attempt < RUN_END_RECHECK_ATTEMPTS) {
        const delay = RUN_END_RECHECK_BASE_MS * 2 ** ending.attempt;
        this.runEndRecheckTimer = this.clock.setTimeout(() => {
          this.runEndRecheckTimer = null;
          if (generation === this.generation) this.confirmRunEnded({ ...ending, attempt: ending.attempt + 1 });
        }, delay);
        return;
      }
      if (!ending.foreign) return;
      try {
        const messages = await this.readThreadMessages(threadId);
        if (generation === this.generation && this.threadId === threadId && this.$status() === 'idle') this.applyStoredMessages(messages, ending.superseded);
      } catch (error) {
        console.warn('[AgentDurable] re-read after a foreign run failed:', error);
      }
    });
  }

  /** A reading that failed, or one that still names a run this window has seen end as running. */
  private isEndedRunStillReported(status: AIDurableThreadStatusResponse | null): boolean {

    if (!status) return true;
    if (status.state !== 'running') return false;
    return !status.runId || this.endedRunIds.has(status.runId);
  }

  private clearRunEndRecheck(): void {

    if (this.runEndRecheckTimer === null) return;
    this.clock.clearTimeout(this.runEndRecheckTimer);
    this.runEndRecheckTimer = null;
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  private handleRouteError(error: DurableRouteError, action: 'send' | 'stop' | 'card'): void {

    switch (error.code) {
      case 'DECIDE_FIRST':
        this.$decideFirstMessage.set(error.message);
        void this.refreshStatus();
        return;
      case 'AGENT_THREAD_LEASE_CONFLICT':
        // Another window started the run: attach to it through the open subscription.
        this.$notice.set(error.message);
        void this.refreshStatus();
        return;
      case 'CARD_EXPIRED':
        this.state.settlePendingApprovals(true);
        this.$notice.set(error.message);
        void this.refreshStatus();
        return;
      case AI_CHAT_CONTEXT_LIMIT_REACHED_CODE:
        // The chat is full, as the stream route reports in its error event: not an error to show.
        // The measurement swaps the composer for the blocked notice that offers a new chat, and the
        // refused words were already handed back.
        if (error.contextStatus) {
          this.applyContextGuardStatus(error.contextStatus);
          return;
        }
        break;
      case DURABLE_USAGE_LIMIT_CODE:
        // The usage-limit door check refused, as it refuses a stale browser on the stream route. Not
        // an error to show: the host re-reads the limit status and swaps the composer for the notice
        // that explains the limit and when the agent is available again.
        this.$usageLimitRejections.update(count => count + 1);
        return;
      default:
        break;
    }
    if (CALM_RETRY_CODES.has(error.code) || action !== 'send') {
      this.$notice.set(error.message);
      return;
    }
    this.$error.set(new Error(error.message));
  }

  private applyContextGuardStatus(status: AIChatContextGuardStatus): void {

    this.$contextGuardStatus.set(status);
    this.state.applyContextGuardStatus(status);
  }

  private async readThreadMessages(threadId: string): Promise<UIMessage[]> {

    const response = await firstValueFrom(this.aiService.getThreadMessages(AgentUtility.AGENT_ID, threadId));
    return AgentUtility.convertToUIMessages(response.uiMessages ?? []);
  }

  private extractText(message: UIMessage): string {

    return (message.parts ?? [])
      .filter(part => part.type === 'text')
      .map(part => AgentUtility.partField<string>(part as AnyPart, 'text', ''))
      .join('\n');
  }

  private extractAttachedFiles(message: UIMessage, blobUrlMap?: Record<string, string>): AIAgentAttachment[] {

    return (message.parts ?? [])
      .filter(part => part.type === 'file')
      .map(part => {
        const filename = AgentUtility.resolveFilename(part as AnyPart);
        const idMatch = filename.match(/^(att_[a-zA-Z0-9_-]+)__/);
        const id = idMatch ? idMatch[1] : undefined;
        const url = AgentUtility.partField<string>(part as AnyPart, 'url', '');
        const mediaType = AgentUtility.partField<string>(part as AnyPart, 'mediaType', '') || AgentUtility.inferMimeType(filename) || 'application/octet-stream';
        if (!id || !url) return null;
        const blobUrl = blobUrlMap?.[id];
        return { id, url, mediaType, ...(filename ? { filename } : {}), ...(blobUrl ? { blobUrl } : {}) };
      })
      .filter((file): file is NonNullable<typeof file> => Boolean(file));
  }

  private parseToolInput(buffer: string): unknown {

    const trimmed = buffer.trim();
    if (!trimmed) return {};
    try {
      return JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  }

  private extractErrorMessage(result: unknown): string {

    if (result instanceof Error) return result.message;
    if (typeof result === 'string') return result;
    if (isRecord(result)) {
      const candidate = result as { message?: string; error?: string; errorText?: string };
      return candidate.message || candidate.error || candidate.errorText || 'Tool execution failed';
    }
    return 'Tool execution failed';
  }
}
